import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkFrontmatter from 'remark-frontmatter';
import { ArrowUp, ArrowDown, ArrowUpDown, ArrowRight, Paperclip, Search as SearchIcon, ExternalLink, Loader2, Unlink, Plus, Receipt, RefreshCw } from 'lucide-react';
import { TAX_CATEGORIES } from '@atek/ca-tax';
import { PageShell, UnderlineTabs, type UnderlineTabsTab } from '@/shared/components';
import { usePersistedTab } from '@/shared/hooks/usePersistedFilters';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Check, X } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { InvoiceMemoryTab } from '@/features/aging/components/InvoiceMemoryTab';
import { InvoiceHistoryTab } from '@/features/aging/components/InvoiceHistoryTab';
import { Label } from '@/components/ui/label';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import AnalysisTab from '../components/AnalysisTab';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { fetchQboInvoices, type QboInvoice } from '../api';
import { api } from '@/lib/api';
import {
  useInvoiceDocuments,
  useAddInvoiceDocument,
  useRemoveInvoiceDocument,
  useUpdateInvoice,
  useInvoiceActivities,
  useAddInvoiceActivity,
} from '@/features/invoices/hooks';
import { searchDocuments, getDocumentDownloadUrl, getDocumentDetail } from '@/features/documents/api';
import { UploadDropzone } from '@/features/documents/components/UploadDropzone';
import { useDocumentDetail, useDocumentPreviewUrl } from '@/features/documents/hooks/useDocuments';
import { getSubscriptionDisplay } from '@/features/invoices/types';
import type {
  V4Invoice,
  V4InvoicesResponse,
  V4InvoiceStatsResponse,
  InvoiceStatus,
  Invoice,
} from '@/features/invoices/types';

// =============================================================================
// localStorage persistence helper
// =============================================================================

const STORAGE_KEY = 'atek-acc-invoices-filters';

interface PersistedFilters {
  tab?: string;
  qbo?: { status?: string; from?: string; to?: string; sortKey?: string; sortDir?: number; pageSize?: number; syncFilter?: string };
  atek?: { status?: string; from?: string; to?: string; sortKey?: string; sortDir?: number; pageSize?: number; syncFilter?: string };
  sync?: {
    invoiceNumber?: string;
    /** @deprecated single account — replaced by `accounts` array. Kept for legacy reads. */
    account?: string;
    accounts?: string[];
    statuses?: string[];
    ams?: string[];
    from?: string;
    to?: string;
    dueDateFrom?: string;
    dueDateTo?: string;
    syncFilter?: string;
    sortKey?: string;
    sortDir?: number;
    pageSize?: number;
  };
}

function loadPersistedFilters(): PersistedFilters {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); }
  catch { return {}; }
}

function savePersistedFilters(patch: Partial<PersistedFilters>) {
  const current = loadPersistedFilters();
  const merged = { ...current, ...patch };
  if (patch.qbo) merged.qbo = { ...current.qbo, ...patch.qbo };
  if (patch.atek) merged.atek = { ...current.atek, ...patch.atek };
  if (patch.sync) merged.sync = { ...current.sync, ...patch.sync };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
}

// =============================================================================
// Normalized invoice row — shared shape for both tabs
// =============================================================================

interface NormalizedInvoice {
  id: string;
  invoiceNumber: string;
  date: string;
  dueDate: string;
  account: string;
  total: number;
  balance: number;
  status: string;
  overdue: boolean;
  currency: string;
  synced: boolean;
  documentCount: number;
  accountManager: string;
}

type SortKey = keyof Pick<NormalizedInvoice, 'invoiceNumber' | 'date' | 'dueDate' | 'account' | 'total' | 'balance' | 'status'>;
type SyncFilter = 'all' | 'synced' | 'unsynced';

// =============================================================================
// Shared components
// =============================================================================

function SummaryCard({
  label,
  value,
  className,
  isCurrency = true,
}: {
  label: string;
  value: number;
  className?: string;
  isCurrency?: boolean;
}) {
  return (
    <div className="bg-background rounded-xl shadow-sm border p-5">
      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        {label}
      </div>
      <div className={cn('text-2xl font-bold mt-2 tabular-nums', className)}>
        {isCurrency ? formatCurrency(value) : value.toLocaleString()}
      </div>
    </div>
  );
}

function SortableHead({
  label,
  sortKey,
  currentSortKey,
  sortDir,
  onSort,
  align,
}: {
  label: string;
  sortKey: SortKey;
  currentSortKey: SortKey;
  sortDir: 1 | -1;
  onSort: (key: SortKey) => void;
  align?: 'left' | 'right';
}) {
  const isActive = currentSortKey === sortKey;
  return (
    <TableHead className={cn(align === 'right' && 'text-right')}>
      <Button
        variant="ghost"
        size="sm"
        className={cn('-ml-3 h-8 hover:bg-transparent', align === 'right' && 'ml-auto -mr-3')}
        onClick={() => onSort(sortKey)}
      >
        {label}
        {isActive ? (
          sortDir === 1 ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />
        ) : (
          <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
        )}
      </Button>
    </TableHead>
  );
}

function SyncDot({ synced, source }: { synced: boolean; source: 'atek' | 'qbo' }) {
  if (synced) {
    const label = source === 'atek' ? 'Synced with QBO' : 'Synced with ATEK';
    const color = source === 'atek' ? 'bg-green-500' : 'bg-purple-500';
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={cn('inline-block h-2.5 w-2.5 rounded-full', color)} />
          </TooltipTrigger>
          <TooltipContent side="top"><p className="text-xs">{label}</p></TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-gray-300" />
        </TooltipTrigger>
        <TooltipContent side="top"><p className="text-xs">Not synced</p></TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function InvoiceRow({ invoice, source, onClick }: { invoice: NormalizedInvoice; source: 'atek' | 'qbo'; onClick?: () => void }) {
  return (
    <TableRow className={onClick ? 'cursor-pointer hover:bg-muted/50' : ''} onClick={onClick}>
      <TableCell className="text-center"><SyncDot synced={invoice.synced} source={source} /></TableCell>
      <TableCell className="font-mono text-xs">{invoice.invoiceNumber}</TableCell>
      <TableCell>{invoice.date ? formatDate(invoice.date) : '—'}</TableCell>
      <TableCell className={cn(invoice.overdue && 'text-red-600 font-medium')}>
        {invoice.dueDate ? formatDate(invoice.dueDate) : '—'}
      </TableCell>
      <TableCell className="max-w-[200px] truncate">{invoice.account || '—'}</TableCell>
      <TableCell className="text-right tabular-nums font-medium">
        {formatCurrency(invoice.total, invoice.currency)}
      </TableCell>
      <TableCell className={cn('text-right tabular-nums', invoice.balance > 0 ? 'text-orange-600' : 'text-muted-foreground')}>
        {formatCurrency(invoice.balance, invoice.currency)}
      </TableCell>
      <TableCell>
        <Badge
          variant="default"
          className={cn(
            invoice.status === 'paid' || invoice.status === 'Paid'
              ? 'bg-green-600 hover:bg-green-700'
              : invoice.overdue
                ? 'bg-red-600 text-white hover:bg-red-700'
                : invoice.status === 'sent' || invoice.status === 'Open' || invoice.status === 'Partial'
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : invoice.status === 'cancelled' || invoice.status === 'Voided'
                    ? 'bg-red-600 hover:bg-red-700'
                    : invoice.status === 'expired'
                      ? 'bg-orange-600 hover:bg-orange-700'
                      : 'bg-gray-500 hover:bg-gray-600',
          )}
        >
          {invoice.overdue ? 'Overdue' : invoice.status}
        </Badge>
      </TableCell>
      <TableCell className="text-center">
        {invoice.documentCount > 0 ? (
          <span className="inline-flex items-center gap-1 text-muted-foreground" title={`${invoice.documentCount} document${invoice.documentCount > 1 ? 's' : ''}`}>
            <Paperclip className="h-3.5 w-3.5" />
            <span className="text-xs">{invoice.documentCount}</span>
          </span>
        ) : '—'}
      </TableCell>
    </TableRow>
  );
}

function InvoicesTable({
  invoices,
  total,
  page,
  pageSize,
  sortKey,
  sortDir,
  onSort,
  onPageChange,
  isLoading,
  error,
  source,
  onRowClick,
}: {
  invoices: NormalizedInvoice[];
  total: number;
  page: number;
  pageSize: number;
  sortKey: SortKey;
  sortDir: 1 | -1;
  onSort: (key: SortKey) => void;
  onPageChange: (page: number) => void;
  isLoading: boolean;
  error: Error | null;
  source: 'atek' | 'qbo';
  onRowClick?: (invoice: NormalizedInvoice) => void;
}) {
  const totalPages = Math.ceil(total / pageSize) || 1;
  const colCount = 9;

  return (
    <>
      <div className="border rounded-lg overflow-hidden bg-background">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10 text-center">Sync</TableHead>
              <SortableHead label="Invoice #" sortKey="invoiceNumber" currentSortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortableHead label="Date" sortKey="date" currentSortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortableHead label="Due" sortKey="dueDate" currentSortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortableHead label="Account" sortKey="account" currentSortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortableHead label="Total" sortKey="total" currentSortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />
              <SortableHead label="Balance" sortKey="balance" currentSortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />
              <SortableHead label="Status" sortKey="status" currentSortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <TableHead className="w-16 text-center">Docs</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 10 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: colCount }).map((__, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : error ? (
              <TableRow>
                <TableCell colSpan={colCount} className="text-center text-red-600 py-8">
                  {error.message}
                </TableCell>
              </TableRow>
            ) : invoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colCount} className="text-center text-muted-foreground py-8">
                  No invoices found.
                </TableCell>
              </TableRow>
            ) : (
              invoices.map((inv) => <InvoiceRow key={inv.id} invoice={inv} source={source} onClick={onRowClick ? () => onRowClick(inv) : undefined} />)
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-muted-foreground">
            Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)} of {total}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => onPageChange(page - 1)} disabled={page === 0}>
              Previous
            </Button>
            <Button variant="outline" size="sm" onClick={() => onPageChange(page + 1)} disabled={page >= totalPages - 1}>
              Next
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

// =============================================================================
// Client-side sort + paginate helper (for QBO tab)
// =============================================================================

function useSortedPaginated(
  items: NormalizedInvoice[],
  sortKey: SortKey,
  sortDir: 1 | -1,
  page: number,
  pageSize: number,
) {
  const sorted = useMemo(() => {
    return [...items].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      let cmp: number;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        cmp = aVal - bVal;
      } else {
        cmp = String(aVal).localeCompare(String(bVal));
      }
      return sortDir === 1 ? cmp : -cmp;
    });
  }, [items, sortKey, sortDir]);

  const total = sorted.length;
  const totalPages = Math.ceil(total / pageSize) || 1;
  const safePage = Math.min(page, totalPages - 1);
  const paged = useMemo(
    () => sorted.slice(safePage * pageSize, (safePage + 1) * pageSize),
    [sorted, safePage, pageSize],
  );

  return { paged, total, safePage };
}

// =============================================================================
// Document Row — shows a single linked document with preview/unlink
// =============================================================================

function DocRow({ documentId, onUnlink, isUnlinking }: {
  documentId: string;
  onUnlink: () => void;
  isUnlinking: boolean;
}) {
  const detailQuery = useDocumentDetail(documentId);
  const previewQuery = useDocumentPreviewUrl(documentId, !!detailQuery.data);
  const doc = detailQuery.data;
  const previewUrl = previewQuery.data?.previewUrl;
  const mimeType = previewQuery.data?.mimeType || doc?.currentVersion?.sourceMimeType || '';
  const content = doc?.currentVersion?.content || '';
  const isImage = mimeType.startsWith('image/');
  const isPdf = mimeType === 'application/pdf';
  // HTML renders natively in an iframe from the GCS signed URL (extensions
  // like storage.douzineapi intercept top-level nav, not iframe embeds).
  const isHtml = mimeType === 'text/html';
  // DMS produces markdown content for text/plain and text/markdown uploads.
  const isTextLike =
    !isImage && !isPdf && !isHtml
    && (
      mimeType === 'text/markdown'
      || mimeType === 'text/plain'
      || (!!content && !mimeType) // fallback: content present, no known binary type
    );

  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Paperclip className="h-4 w-4 text-muted-foreground shrink-0" />
          <p className="text-sm font-medium truncate">
            {detailQuery.isLoading ? 'Loading...' : doc?.name || 'Unknown document'}
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {previewUrl && !isTextLike && !isHtml && (
            <a
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-primary p-1"
              title="Open in new tab"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
          <button
            type="button"
            onClick={() => { if (window.confirm('Unlink this document from the invoice?')) onUnlink(); }}
            disabled={isUnlinking}
            className="text-muted-foreground hover:text-destructive p-1"
            title="Unlink document"
          >
            <Unlink className="h-4 w-4" />
          </button>
        </div>
      </div>
      {previewUrl && isImage && (
        <div className="px-3 pb-3">
          <img src={previewUrl} alt={doc?.name || 'Preview'} className="w-full rounded border object-contain max-h-48" />
        </div>
      )}
      {previewUrl && isPdf && (
        <div className="px-3 pb-3">
          <iframe src={previewUrl} title={doc?.name || 'Preview'} className="w-full h-64 rounded border" />
        </div>
      )}
      {previewUrl && isHtml && (
        <div className="px-3 pb-3">
          <iframe
            src={previewUrl}
            title={doc?.name || 'Preview'}
            className="w-full h-96 rounded border bg-white"
            sandbox="allow-same-origin allow-popups"
          />
        </div>
      )}
      {isTextLike && content && (
        <div className="px-3 pb-3">
          <div className="prose prose-sm max-w-none border rounded p-3 bg-muted/30 max-h-96 overflow-y-auto">
            <ReactMarkdown remarkPlugins={[remarkGfm, remarkFrontmatter]}>
              {content}
            </ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Document Slot — typed reference-document row matching DMS docs by name keyword
// =============================================================================

interface InvoiceDocLink {
  documentId: string;
  id: string;
}

/**
 * One linked DMS document inside a typed slot. Returns null if the document
 * name doesn't match the slot's keywords (used as a soft "tag" until the
 * invoice schema gets explicit FK columns: po_document_id, contract_id, etc.).
 */
function DocumentSlotItem({
  documentId, keywords, onUnlink, isUnlinking, onMatch,
}: {
  documentId: string;
  keywords: string[];
  onUnlink: () => void;
  isUnlinking: boolean;
  /** Called once when match status is known so the parent can hide its empty placeholder. */
  onMatch: (matched: boolean) => void;
}) {
  const detail = useDocumentDetail(documentId);
  const name = detail.data?.name ?? '';
  const lowerName = name.toLowerCase();
  const isLoading = detail.isLoading;
  const matched = !isLoading && keywords.some((k) => lowerName.includes(k.toLowerCase()));
  // Lazy-fetch the signed GCS preview URL only once we know this doc matches a slot.
  // There is NO `/documents/:id` route in frontend-react — the only way to open a
  // single document is via its signed preview URL from /v4/dms/.../preview-url.
  const previewQuery = useDocumentPreviewUrl(documentId, matched);
  const previewUrl = previewQuery.data?.previewUrl;

  useEffect(() => {
    if (!isLoading) onMatch(matched);
  }, [isLoading, matched, onMatch]);

  if (!matched) return null;

  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 bg-muted/50 rounded-md text-xs">
      <Paperclip className="h-3 w-3 text-muted-foreground" />
      {previewUrl ? (
        <a
          href={previewUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline truncate max-w-[200px]"
          title={name}
        >
          {name}
        </a>
      ) : (
        <span className="text-muted-foreground truncate max-w-[200px]" title={name}>
          {name} {previewQuery.isLoading ? '(loading…)' : ''}
        </span>
      )}
      <button
        type="button"
        onClick={onUnlink}
        disabled={isUnlinking}
        className="text-muted-foreground hover:text-destructive disabled:opacity-50"
        title="Unlink"
      >
        <Unlink className="h-3 w-3" />
      </button>
    </span>
  );
}

/**
 * Reference-document slot — one labeled row for a typed document type
 * (Quote PDF, PO Document, Contract, Invoice PDF, Payment Notification, Bank Hit).
 *
 * Filters the invoice's linked DMS documents by name keyword (case-insensitive).
 * Once the invoice schema has typed FK columns (e.g. po_document_id), replace
 * the keyword heuristic with a direct field read.
 */
function DocumentSlot({
  label, hint, keywords, documents, onUnlink, isUnlinking, onAttach,
}: {
  label: string;
  hint?: string;
  keywords: string[];
  documents: InvoiceDocLink[];
  onUnlink: (documentId: string) => void;
  isUnlinking: boolean;
  onAttach: () => void;
}) {
  // Track which children matched so we can hide the "No file linked yet" placeholder.
  const [matchedIds, setMatchedIds] = useState<Set<string>>(new Set());
  const handleMatch = useCallback((documentId: string) => (matched: boolean) => {
    setMatchedIds((prev) => {
      const had = prev.has(documentId);
      if (matched && had) return prev;
      if (!matched && !had) return prev;
      const next = new Set(prev);
      if (matched) next.add(documentId);
      else next.delete(documentId);
      return next;
    });
  }, []);

  // Reset matched set if the linked-docs list shrinks or refreshes.
  useEffect(() => {
    const linkedIds = new Set(documents.map((d) => d.documentId));
    setMatchedIds((prev) => {
      let changed = false;
      const next = new Set<string>();
      prev.forEach((id) => {
        if (linkedIds.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [documents]);

  const hasMatches = matchedIds.size > 0;

  return (
    <div className="px-3 py-2 flex items-start gap-3">
      <div className="w-32 shrink-0 pt-1">
        <p className="text-xs font-medium text-foreground">{label}</p>
        {hint && <p className="text-[10px] text-muted-foreground mt-0.5">{hint}</p>}
      </div>
      <div className="flex-1 min-w-0 flex flex-wrap items-center gap-2 min-h-7">
        {documents.map((d) => (
          <DocumentSlotItem
            key={d.documentId}
            documentId={d.documentId}
            keywords={keywords}
            onUnlink={() => onUnlink(d.documentId)}
            isUnlinking={isUnlinking}
            onMatch={handleMatch(d.documentId)}
          />
        ))}
        {!hasMatches && (
          <span className="text-xs text-muted-foreground">No file linked yet</span>
        )}
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 text-xs shrink-0"
        onClick={onAttach}
        title={`Attach a ${label} document`}
      >
        <Plus className="h-3 w-3 mr-1" /> Attach
      </Button>
    </div>
  );
}

// =============================================================================
// Documents Sheet — slide-out panel for managing invoice documents
// =============================================================================

function DocumentsSheet({
  open,
  onOpenChange,
  invoiceId,
  invoiceNumber,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceId: string;
  invoiceNumber: string;
}) {
  const [mode, setMode] = useState<'list' | 'search'>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{ id: string; name: string }>>([]);
  const [isSearching, setIsSearching] = useState(false);

  const documentsQuery = useInvoiceDocuments(invoiceId);
  const addDocMutation = useAddInvoiceDocument();
  const removeDocMutation = useRemoveInvoiceDocument();

  const documents = documentsQuery.data || [];
  const linkedDocIds = new Set(documents.map((d) => d.documentId));

  // DMS search
  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    if (!query.trim()) { setSearchResults([]); return; }
    setIsSearching(true);
    try {
      const results = await searchDocuments(query);
      setSearchResults(results.data || []);
    } catch { setSearchResults([]); }
    finally { setIsSearching(false); }
  };

  // Auto-link document after upload via UploadDropzone
  const handleUploaded = async (documentId: string) => {
    await addDocMutation.mutateAsync({ invoiceId, documentId });
  };

  // Link existing DMS document
  const handleLinkDocument = async (documentId: string) => {
    await addDocMutation.mutateAsync({ invoiceId, documentId });
    setSearchQuery('');
    setSearchResults([]);
    setMode('list');
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Documents — {invoiceNumber}</SheetTitle>
        </SheetHeader>

        <div className="space-y-4 mt-6">
          {/* Upload via reusable DMS UploadDropzone */}
          <UploadDropzone onUploaded={handleUploaded} />

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => setMode(mode === 'search' ? 'list' : 'search')}
          >
            <SearchIcon className="h-4 w-4 mr-1" />
            {mode === 'search' ? 'Cancel search' : 'Pick from DMS'}
          </Button>

          {/* DMS Search */}
          {mode === 'search' && (
            <div className="space-y-2">
              <Input
                placeholder="Search documents in DMS..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                autoFocus
              />
              {isSearching && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Searching...
                </div>
              )}
              {searchResults.length > 0 && (
                <div className="space-y-1 max-h-48 overflow-y-auto border rounded p-2">
                  {searchResults.map((doc) => (
                    <button
                      key={doc.id}
                      type="button"
                      onClick={() => handleLinkDocument(doc.id)}
                      disabled={linkedDocIds.has(doc.id) || addDocMutation.isPending}
                      className="w-full text-left flex items-center justify-between p-2 rounded hover:bg-muted text-sm disabled:opacity-50"
                    >
                      <span className="truncate min-w-0 flex-1">{doc.name}</span>
                      {linkedDocIds.has(doc.id) ? (
                        <span className="text-xs text-muted-foreground ml-2">Linked</span>
                      ) : (
                        <Plus className="h-4 w-4 ml-2 shrink-0 text-muted-foreground" />
                      )}
                    </button>
                  ))}
                </div>
              )}
              {searchQuery && !isSearching && searchResults.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-2">No documents found</p>
              )}
            </div>
          )}

          {/* Linked Documents List */}
          <div className="space-y-2">
            <Label>Linked Documents ({documents.length})</Label>
            {documentsQuery.isLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-4 justify-center">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading...
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center text-muted-foreground py-6 text-sm border rounded-lg">
                No documents linked yet
              </div>
            ) : (
              <div className="space-y-2">
                {documents.map((doc) => (
                  <DocRow
                    key={doc.id}
                    documentId={doc.documentId}
                    onUnlink={() => { if (window.confirm('Unlink this document from the invoice?')) removeDocMutation.mutate({ invoiceId, documentId: doc.documentId }); }}
                    isUnlinking={removeDocMutation.isPending}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// =============================================================================
// QBO Tab
// =============================================================================

type QboStatusFilter = 'all' | 'open' | 'paid' | 'overdue';

function QboTab({ atekInvoiceNumbers }: { atekInvoiceNumbers: Set<string> }) {
  const saved = useMemo(() => loadPersistedFilters().qbo || {}, []);
  const [status, setStatusRaw] = useState<QboStatusFilter>((saved.status as QboStatusFilter) || 'open');
  const [search, setSearch] = useState('');
  const [from, setFromRaw] = useState(saved.from || '');
  const [selectedInvoice, setSelectedInvoice] = useState<NormalizedInvoice | null>(null);
  const [to, setToRaw] = useState(saved.to || '');
  const [sortKey, setSortKeyRaw] = useState<SortKey>((saved.sortKey as SortKey) || 'date');
  const [sortDir, setSortDirRaw] = useState<1 | -1>((saved.sortDir as 1 | -1) || -1);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSizeRaw] = useState(saved.pageSize || 25);
  const [syncFilter, setSyncFilterRaw] = useState<SyncFilter>((saved.syncFilter as SyncFilter) || 'all');

  const persist = useCallback((patch: Partial<PersistedFilters['qbo']>) => savePersistedFilters({ qbo: patch }), []);
  const setStatus = (v: QboStatusFilter) => { setStatusRaw(v); persist({ status: v }); };
  const setFrom = (v: string) => { setFromRaw(v); persist({ from: v }); };
  const setTo = (v: string) => { setToRaw(v); persist({ to: v }); };
  const setSortKey = (v: SortKey) => { setSortKeyRaw(v); persist({ sortKey: v }); };
  const setSortDir = (v: 1 | -1) => { setSortDirRaw(v); persist({ sortDir: v }); };
  const setPageSize = (v: number) => { setPageSizeRaw(v); persist({ pageSize: v }); };
  const setSyncFilter = (v: SyncFilter) => { setSyncFilterRaw(v); persist({ syncFilter: v }); };

  const { data, isLoading, error } = useQuery({
    queryKey: ['quickbooks-invoices', from, to, status],
    queryFn: () =>
      fetchQboInvoices({
        start: from || undefined,
        end: to || undefined,
        status: status === 'all' ? undefined : status,
      }),
    staleTime: 0,
  });

  const handleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir(sortDir === 1 ? -1 : 1);
    else { setSortKey(key); setSortDir(-1); }
    setPage(0);
  };

  const normalized = useMemo<NormalizedInvoice[]>(() => {
    const list = data?.invoices ?? [];
    let result = list;
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (i) =>
          i.docNumber.toLowerCase().includes(q) ||
          i.customerName.toLowerCase().includes(q) ||
          i.memo.toLowerCase().includes(q),
      );
    }
    const today = new Date().toISOString().slice(0, 10);
    let mapped = result.map((i) => ({
      id: i.id,
      invoiceNumber: i.docNumber,
      date: i.date,
      dueDate: i.dueDate,
      account: i.customerName,
      total: i.total,
      balance: i.balance,
      status: i.status,
      overdue: i.balance > 0 && !!i.dueDate && i.dueDate < today,
      currency: i.currency || 'CAD',
      synced: atekInvoiceNumbers.has(i.docNumber),
      documentCount: 0,
      accountManager: '',
    }));
    // Sync filter
    if (syncFilter === 'synced') mapped = mapped.filter((i) => i.synced);
    else if (syncFilter === 'unsynced') mapped = mapped.filter((i) => !i.synced);
    return mapped;
  }, [data, search, atekInvoiceNumbers, syncFilter]);

  const { paged, total, safePage } = useSortedPaginated(normalized, sortKey, sortDir, page, pageSize);

  const totals = useMemo(() => {
    return normalized.reduce(
      (acc, i) => ({
        total: acc.total + i.total,
        balance: acc.balance + i.balance,
        paid: acc.paid + (i.total - i.balance),
      }),
      { total: 0, balance: 0, paid: 0 },
    );
  }, [normalized]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-4">
        <SummaryCard label="Count" value={normalized.length} isCurrency={false} />
        <SummaryCard label="Total" value={totals.total} />
        <SummaryCard label="Paid" value={totals.paid} className="text-green-600" />
        <SummaryCard label="Balance" value={totals.balance} className="text-orange-600" />
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        <div className="space-y-1">
          <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Search</label>
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            placeholder="Customer, doc #…"
            className="w-64"
          />
        </div>
        <div className="space-y-1">
          <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Status</label>
          <Select value={status} onValueChange={(v) => { setStatus(v as QboStatusFilter); setPage(0); }}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Sync</label>
          <Select value={syncFilter} onValueChange={(v) => { setSyncFilter(v as SyncFilter); setPage(0); }}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="synced">
                <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-full bg-purple-500" /> Synced</span>
              </SelectItem>
              <SelectItem value="unsynced">
                <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-full bg-gray-300" /> Not synced</span>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-[11px] uppercase tracking-wide text-muted-foreground">From</label>
          <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(0); }} />
        </div>
        <div className="space-y-1">
          <label className="text-[11px] uppercase tracking-wide text-muted-foreground">To</label>
          <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(0); }} />
        </div>
        <div className="space-y-1">
          <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Per page</label>
          <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(0); }}>
            <SelectTrigger className="w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <InvoicesTable
        invoices={paged}
        total={total}
        page={safePage}
        pageSize={pageSize}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={handleSort}
        onPageChange={setPage}
        isLoading={isLoading}
        error={error as Error | null}
        source="qbo"
        onRowClick={setSelectedInvoice}
      />

      {selectedInvoice && (
        <DocumentsSheet
          open={!!selectedInvoice}
          onOpenChange={(open) => { if (!open) setSelectedInvoice(null); }}
          invoiceId={selectedInvoice.id}
          invoiceNumber={selectedInvoice.invoiceNumber}
        />
      )}
    </div>
  );
}

// =============================================================================
// ATEK Tab
// =============================================================================

const ATEK_SORT_MAP: Record<SortKey, string> = {
  invoiceNumber: 'invoiceNumber',
  date: 'invoiceDate',
  dueDate: 'dueDate',
  account: 'accountName',
  total: 'total',
  balance: 'total',
  status: 'status',
};

function AtekTab({ qboInvoiceNumbers }: { qboInvoiceNumbers: Set<string> }) {
  const saved = useMemo(() => loadPersistedFilters().atek || {}, []);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState<NormalizedInvoice | null>(null);
  const [selectedStatuses, setSelectedStatusesRaw] = useState<InvoiceStatus[]>(() => {
    if (saved.status && saved.status !== 'all') return [saved.status as InvoiceStatus];
    return [];
  });
  const [from, setFromRaw] = useState(saved.from || '');
  const [to, setToRaw] = useState(saved.to || '');
  const [sortKey, setSortKeyRaw] = useState<SortKey>((saved.sortKey as SortKey) || 'date');
  const [sortDir, setSortDirRaw] = useState<1 | -1>((saved.sortDir as 1 | -1) || -1);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSizeRaw] = useState(saved.pageSize || 25);
  const [syncFilter, setSyncFilterRaw] = useState<SyncFilter>((saved.syncFilter as SyncFilter) || 'all');

  const persist = useCallback((patch: Partial<PersistedFilters['atek']>) => savePersistedFilters({ atek: patch }), []);
  const setSelectedStatuses = (v: InvoiceStatus[]) => { setSelectedStatusesRaw(v); persist({ status: v.length === 1 ? v[0] : 'all' }); };
  const setFrom = (v: string) => { setFromRaw(v); persist({ from: v }); };
  const setTo = (v: string) => { setToRaw(v); persist({ to: v }); };
  const setSortKey = (v: SortKey) => { setSortKeyRaw(v); persist({ sortKey: v }); };
  const setSortDir = (v: 1 | -1) => { setSortDirRaw(v); persist({ sortDir: v }); };
  const setPageSize = (v: number) => { setPageSizeRaw(v); persist({ pageSize: v }); };
  const setSyncFilter = (v: SyncFilter) => { setSyncFilterRaw(v); persist({ syncFilter: v }); };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const queryParams: Record<string, string | number> = {
    pageNumber: page,
    pageLength: pageSize,
    sortKey: ATEK_SORT_MAP[sortKey] || 'createdAt',
    sortDirection: sortDir,
  };
  if (debouncedSearch) queryParams.search = debouncedSearch;
  if (selectedStatuses.length > 0) queryParams.status_in = JSON.stringify(selectedStatuses);
  if (from) queryParams.dateFrom = from;
  if (to) queryParams.dateTo = to;

  const { data, isLoading, error } = useQuery({
    queryKey: ['atek-invoices', queryParams],
    queryFn: () => api.get<V4InvoicesResponse>('/v4/invoices', queryParams),
    staleTime: 0,
  });

  const statsQuery = useQuery({
    queryKey: ['atek-invoices-stats'],
    queryFn: () => api.get<V4InvoiceStatsResponse>('/v4/invoices/stats'),
    staleTime: 0,
  });

  const handleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir(sortDir === 1 ? -1 : 1);
    else { setSortKey(key); setSortDir(-1); }
    setPage(0);
  };

  const today = new Date().toISOString().slice(0, 10);
  const invoices = useMemo<NormalizedInvoice[]>(() => {
    let result = (data?.invoices ?? []).map((i) => {
      const totalNum = parseFloat(i.total);
      const isPaid = i.status === 'paid';
      const isCancelled = i.status === 'cancelled';
      const balance = isPaid || isCancelled ? 0 : totalNum;
      const overdue = i.status === 'sent' && !!i.dueDate && i.dueDate < today;
      const synced = !!i.quickbooksInvoiceId || qboInvoiceNumbers.has(i.invoiceNumber);
      return {
        id: i.id,
        invoiceNumber: i.invoiceNumber,
        date: i.invoiceDate || '',
        dueDate: i.dueDate || i.expirationDate || '',
        account: i.accountName || '',
        total: totalNum,
        balance,
        status: i.status,
        overdue,
        currency: i.currency || 'CAD',
        synced,
        documentCount: i.documentCount ?? 0,
        accountManager: i.salesManagerName || '',
      };
    });
    if (syncFilter === 'synced') result = result.filter((i) => i.synced);
    else if (syncFilter === 'unsynced') result = result.filter((i) => !i.synced);
    return result;
  }, [data, today, qboInvoiceNumbers, syncFilter]);

  const total = syncFilter === 'all' ? (data?.total ?? 0) : invoices.length;

  const stats = statsQuery.data?.stats ?? [];
  const totalStat = stats.find((s) => s.status === 'total');
  const paidStat = stats.find((s) => s.status === 'paid');
  const openAmount = stats
    .filter((s) => s.status !== 'total' && s.status !== 'paid' && s.status !== 'cancelled')
    .reduce((acc, s) => acc + s.amount, 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-4">
        <SummaryCard label="Count" value={total} isCurrency={false} />
        <SummaryCard label="Total" value={totalStat?.amount ?? 0} />
        <SummaryCard label="Paid" value={paidStat?.amount ?? 0} className="text-green-600" />
        <SummaryCard label="Outstanding" value={openAmount} className="text-orange-600" />
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        <div className="space-y-1">
          <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Search</label>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Invoice #, account, project…"
            className="w-64"
          />
        </div>
        <div className="space-y-1">
          <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Status</label>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-40 justify-start font-normal">
                {selectedStatuses.length === 0 ? 'All' : `${selectedStatuses.length} selected`}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-40">
              {([
                { value: 'preparation' as const, label: 'Preparation' },
                { value: 'sent' as const, label: 'Sent' },
                { value: 'paid' as const, label: 'Paid' },
                { value: 'expired' as const, label: 'Expired' },
                { value: 'cancelled' as const, label: 'Cancelled' },
              ]).map((opt) => (
                <DropdownMenuCheckboxItem
                  key={opt.value}
                  checked={selectedStatuses.includes(opt.value)}
                  onCheckedChange={() => {
                    const next = selectedStatuses.includes(opt.value)
                      ? selectedStatuses.filter((s) => s !== opt.value)
                      : [...selectedStatuses, opt.value];
                    setSelectedStatuses(next);
                    setPage(0);
                  }}
                  onSelect={(e) => e.preventDefault()}
                >
                  {opt.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="space-y-1">
          <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Sync</label>
          <Select value={syncFilter} onValueChange={(v) => { setSyncFilter(v as SyncFilter); setPage(0); }}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="synced">
                <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-full bg-green-500" /> In QBO</span>
              </SelectItem>
              <SelectItem value="unsynced">
                <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-full bg-gray-300" /> Not in QBO</span>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-[11px] uppercase tracking-wide text-muted-foreground">From</label>
          <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(0); }} />
        </div>
        <div className="space-y-1">
          <label className="text-[11px] uppercase tracking-wide text-muted-foreground">To</label>
          <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(0); }} />
        </div>
        <div className="space-y-1">
          <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Per page</label>
          <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(0); }}>
            <SelectTrigger className="w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <InvoicesTable
        invoices={invoices}
        total={total}
        page={page}
        pageSize={pageSize}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={handleSort}
        onPageChange={setPage}
        isLoading={isLoading}
        error={error as Error | null}
        source="atek"
        onRowClick={setSelectedInvoice}
      />

      {selectedInvoice && (
        <DocumentsSheet
          open={!!selectedInvoice}
          onOpenChange={(open) => { if (!open) setSelectedInvoice(null); }}
          invoiceId={selectedInvoice.id}
          invoiceNumber={selectedInvoice.invoiceNumber}
        />
      )}
    </div>
  );
}

// =============================================================================
// Sync Tab — side-by-side comparison
// =============================================================================

export interface QboInvoiceDetail extends QboInvoice {
  lines: Array<{ id: string; lineNum: number; description: string; amount: number; quantity: number; unitPrice: number; itemName: string; sku: string }>;
  taxDetail: { TotalTax: number; TaxLine?: Array<{ TaxLineDetail?: { TaxPercent?: number } }> } | null;
  billingAddress: string;
  shippingAddress: string;
  poNumber: string;
}

// Status equivalence: ATEK "sent" = QBO "Open", ATEK "paid" = QBO "Paid"
const STATUS_EQUIVALENTS: Record<string, string> = {
  sent: 'Open',
  paid: 'Paid',
  preparation: 'Open',
  cancelled: 'Voided',
};

function ComparisonRow({ label, mongoValue, v4Value, qboValue, matchOverride, onSyncToQbo, action }: { label: string; mongoValue: string; v4Value: string; qboValue: string; matchOverride?: boolean; onSyncToQbo?: () => void; action?: React.ReactNode }) {
  // Drift detection: if any pair of populated values disagrees, flag it
  const present = [mongoValue, v4Value, qboValue].filter((v) => v && v.length > 0);
  const allMatch = matchOverride ?? (present.length > 1 && present.every((v) => v === present[0]));
  const hasDrift = present.length > 1 && !allMatch;
  const mongoVsV4Drift = mongoValue && v4Value && mongoValue !== v4Value;
  return (
    <TableRow className={cn(hasDrift && 'bg-red-50')}>
      <TableCell className="font-medium text-muted-foreground text-xs w-32">{label}</TableCell>
      <TableCell className={cn('text-sm whitespace-normal', mongoVsV4Drift && 'text-amber-700 font-medium')}>{mongoValue || '—'}</TableCell>
      <TableCell className={cn('text-sm whitespace-normal', mongoVsV4Drift && 'text-amber-700 font-medium')}>{v4Value || '—'}</TableCell>
      <TableCell className="text-sm whitespace-normal">{qboValue || '—'}</TableCell>
      <TableCell className="text-xs whitespace-normal">{action || null}</TableCell>
      <TableCell className="w-10 text-center flex items-center gap-0.5 justify-center">
        {present.length > 1 ? (allMatch ? <Check className="h-4 w-4 text-green-600" /> : <X className="h-4 w-4 text-red-500" />) : null}
        {onSyncToQbo && (mongoValue || v4Value) && (
          <button type="button" onClick={onSyncToQbo} className="text-muted-foreground hover:text-blue-600 p-0.5" title="Sync to QuickBooks">
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        )}
      </TableCell>
    </TableRow>
  );
}

interface QboCustomer { id: string; name: string }

function AccountComparisonRow({ mongoAccountName, v4AccountName, qboCustomerName, qboInvoiceId, atekInvoicePgId }: { mongoAccountName: string; v4AccountName: string; qboCustomerName: string; qboInvoiceId: string | null; atekInvoicePgId: string | null }) {
  // QBO side
  const [qboOpen, setQboOpen] = useState(false);
  const [qboSearch, setQboSearch] = useState('');
  const [updatingQbo, setUpdatingQbo] = useState(false);
  const [currentQboName, setCurrentQboName] = useState(qboCustomerName);

  // ATEK V4 side (editable — reassigns the PG account)
  const [atekOpen, setAtekOpen] = useState(false);
  const [atekSearch, setAtekSearch] = useState('');
  const [updatingAtek, setUpdatingAtek] = useState(false);
  const [currentAtekName, setCurrentAtekName] = useState(v4AccountName);

  const queryClient = useQueryClient();

  useEffect(() => { setCurrentQboName(qboCustomerName); }, [qboCustomerName]);
  useEffect(() => { setCurrentAtekName(v4AccountName); }, [v4AccountName]);

  // Fetch QBO customers
  const qboCustomersQuery = useQuery({
    queryKey: ['qbo-customers'],
    queryFn: () => api.get<{ customers: QboCustomer[] }>('/v4/quickbooks/customers/list'),
    staleTime: 0,
  });
  const qboCustomers = qboCustomersQuery.data?.customers ?? [];
  const qboFiltered = useMemo(() => {
    if (!qboSearch.trim()) return qboCustomers.slice(0, 50);
    const q = qboSearch.toLowerCase();
    return qboCustomers.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 50);
  }, [qboCustomers, qboSearch]);

  // Fetch all ATEK accounts (paginate through all pages)
  const atekAccountsQuery = useQuery({
    queryKey: ['atek-accounts-list'],
    queryFn: async () => {
      const all: Array<{ id: string; name: string }> = [];
      let page = 0;
      const pageSize = 100;
      while (true) {
        const res = await api.get<{ accounts: Array<{ id: string; name: string; accountNumber?: string }> }>('/v4/accounts', { type: 'customer', status: 'active', pageLength: pageSize, pageNumber: page, sortKey: 'name', sortDirection: 1 });
        const batch = (res.accounts || []).filter((a) => !a.accountNumber?.startsWith('A-'));
        all.push(...batch);
        if (batch.length < pageSize) break;
        page++;
      }
      return all;
    },
    staleTime: 0,
  });
  const atekAccounts = atekAccountsQuery.data ?? [];
  const atekFiltered = useMemo(() => {
    if (!atekSearch.trim()) return atekAccounts.slice(0, 50);
    const q = atekSearch.toLowerCase();
    return atekAccounts.filter((a) => a.name.toLowerCase().includes(q)).slice(0, 50);
  }, [atekAccounts, atekSearch]);

  // QBO customer update
  const handleSelectQbo = async (customer: QboCustomer) => {
    setQboOpen(false);
    if (!qboInvoiceId) return;
    setUpdatingQbo(true);
    try {
      await api.patch(`/v4/quickbooks/${qboInvoiceId}/customer`, { customerId: customer.id, customerName: customer.name });
      setCurrentQboName(customer.name);
      queryClient.invalidateQueries({ queryKey: ['quickbooks-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['qbo-invoice-detail', qboInvoiceId] });
      toast.success(`QBO customer → ${customer.name}`);
    } catch (e) {
      toast.error('Failed to update QBO customer', { description: (e as Error).message });
    } finally { setUpdatingQbo(false); }
  };

  // ATEK account reassign
  const handleSelectAtek = async (account: { id: string; name: string }) => {
    setAtekOpen(false);
    if (!atekInvoicePgId) return;
    setUpdatingAtek(true);
    try {
      await api.patch(`/v4/invoices/${atekInvoicePgId}/reassign`, { accountId: account.id });
      setCurrentAtekName(account.name);
      queryClient.invalidateQueries({ queryKey: ['atek-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['atek-invoices-sync'] });
      toast.success(`ATEK account → ${account.name}`);
    } catch (e) {
      toast.error('Failed to reassign ATEK invoice', { description: (e as Error).message });
    } finally { setUpdatingAtek(false); }
  };

  const stripPrefix = (s: string) => s.replace(/^\d[\d-]*\s+/, '');
  const match = (() => {
    if (!currentAtekName || !currentQboName) return false;
    if (currentAtekName === currentQboName) return true;
    const a = currentAtekName.toLowerCase();
    const b = currentQboName.toLowerCase();
    return stripPrefix(a) === stripPrefix(b) || a.includes(stripPrefix(b)) || b.includes(stripPrefix(a));
  })();

  const mongoVsV4Drift = mongoAccountName && currentAtekName && mongoAccountName !== currentAtekName;
  return (
    <TableRow className={cn((!match && currentAtekName && currentQboName) || mongoVsV4Drift ? 'bg-red-50' : '')}>
      <TableCell className="font-medium text-muted-foreground text-xs w-32">Account</TableCell>
      {/* Mongo side — read-only customer.name (legacy organisation) */}
      <TableCell className={cn('text-sm whitespace-normal', mongoVsV4Drift && 'text-amber-700 font-medium')}>{mongoAccountName || '—'}</TableCell>
      {/* V4 side — editable (reassigns PG account) */}
      <TableCell className="text-sm">
        <Popover open={atekOpen} onOpenChange={setAtekOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className={cn('h-auto min-h-7 w-full text-xs justify-start font-normal max-w-[220px] whitespace-normal text-left py-1', mongoVsV4Drift && 'text-amber-700')} disabled={updatingAtek || !atekInvoicePgId}>
              {updatingAtek ? 'Reassigning...' : currentAtekName || '— Select account —'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0" align="start">
            <Command shouldFilter={false}>
              <CommandInput placeholder="Search ATEK accounts..." value={atekSearch} onValueChange={setAtekSearch} className="h-8 text-xs" />
              <CommandList>
                <CommandEmpty className="py-2 text-center text-xs text-muted-foreground">No accounts found</CommandEmpty>
                <CommandGroup className="max-h-48 overflow-y-auto">
                  {atekFiltered.map((a) => (
                    <CommandItem key={a.id} value={a.id} onSelect={() => handleSelectAtek(a)} className="text-xs">{a.name}</CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </TableCell>
      {/* QBO side — editable */}
      <TableCell className="text-sm">
        <Popover open={qboOpen} onOpenChange={setQboOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-auto min-h-7 w-full text-xs justify-start font-normal max-w-[220px] whitespace-normal text-left py-1" disabled={updatingQbo || !qboInvoiceId}>
              {updatingQbo ? 'Updating...' : currentQboName || '— Select customer —'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0" align="start">
            <Command shouldFilter={false}>
              <CommandInput placeholder="Search QBO customers..." value={qboSearch} onValueChange={setQboSearch} className="h-8 text-xs" />
              <CommandList>
                <CommandEmpty className="py-2 text-center text-xs text-muted-foreground">No customers found</CommandEmpty>
                <CommandGroup className="max-h-48 overflow-y-auto">
                  {qboFiltered.map((c) => (
                    <CommandItem key={c.id} value={c.id} onSelect={() => handleSelectQbo(c)} className="text-xs">{c.name}</CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </TableCell>
      <TableCell className="text-xs" />
      <TableCell className="w-10 text-center">
        {match ? <Check className="h-4 w-4 text-green-600 inline" /> : currentAtekName && currentQboName ? <X className="h-4 w-4 text-red-500 inline" /> : null}
      </TableCell>
    </TableRow>
  );
}

export function ComparisonSheet({
  open,
  onOpenChange,
  atekInvoice,
  atekV4,
  qboInvoice,
  isLoadingAtek,
  isLoadingQbo,
  invoiceId,
  invoiceNumber,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  atekInvoice: Invoice | null;
  atekV4: V4Invoice | null;
  qboInvoice: QboInvoiceDetail | null;
  isLoadingAtek: boolean;
  isLoadingQbo: boolean;
  invoiceId: string | null;
  invoiceNumber: string;
}) {
  const loading = isLoadingAtek || isLoadingQbo;
  const updateInvoiceMutation = useUpdateInvoice();
  const [voidingQbo, setVoidingQbo] = useState(false);
  const [creatingInQbo, setCreatingInQbo] = useState(false);
  const [sheetTab, setSheetTab] = useState<'details' | 'memory' | 'history'>('details');
  useEffect(() => { setSheetTab('details'); }, [invoiceNumber]);

  // Get the MongoDB invoice ID for updates
  const mongoInvoiceId = atekInvoice?._id || null;

  const queryClient = useQueryClient();

  const handleUpdateField = async (field: string, value: string | null) => {
    if (!mongoInvoiceId) { toast.error('Cannot update — MongoDB invoice not loaded'); return; }
    try {
      await updateInvoiceMutation.mutateAsync({ invoiceId: mongoInvoiceId, data: { [field]: value || undefined } });
      // Refetch ATEK invoice data in the comparison sheet
      queryClient.invalidateQueries({ queryKey: ['atek-invoice-detail', mongoInvoiceId] });
      queryClient.invalidateQueries({ queryKey: ['atek-invoices-sync'] });
      queryClient.invalidateQueries({ queryKey: ['atek-invoices'] });
      toast.success(`ATEK ${field} updated`);

      // Sync status change to QBO
      if (field === 'status' && qboInvoice?.id) {
        if (value === 'paid') {
          try {
            await api.post(`/v4/quickbooks/${qboInvoice.id}/payment`, {
              paymentDate: atekInvoice?.payment_date?.slice(0, 10) || undefined,
              paymentRef: atekInvoice?.payment_ref || undefined,
            });
            queryClient.invalidateQueries({ queryKey: ['quickbooks-invoices'] });
            queryClient.invalidateQueries({ queryKey: ['qbo-invoice-detail', qboInvoice.id] });
            toast.success('QBO payment recorded');
          } catch (e) {
            toast.warning('ATEK updated but QBO payment failed', { description: (e as Error).message });
          }
        } else if (value === 'cancelled') {
          if (window.confirm('Also void this invoice in QuickBooks?')) {
            try {
              await api.post(`/v4/quickbooks/${qboInvoice.id}/void`, {});
              queryClient.invalidateQueries({ queryKey: ['quickbooks-invoices'] });
              queryClient.invalidateQueries({ queryKey: ['qbo-invoice-detail', qboInvoice.id] });
              toast.success('QBO invoice voided');
            } catch (e) {
              toast.warning('ATEK cancelled but QBO void failed', { description: (e as Error).message });
            }
          }
        }
      }
    } catch (e) {
      toast.error(`Failed to update ${field}`, { description: (e as Error).message });
    }
  };

  // Push a field from ATEK to QBO via sparse invoice update
  const handleSyncFieldToQbo = async (qboField: string, value: string) => {
    if (!qboInvoice?.id) { toast.error('No QBO invoice linked'); return; }
    try {
      await api.patch(`/v4/quickbooks/${qboInvoice.id}`, { [qboField]: value });
      queryClient.invalidateQueries({ queryKey: ['quickbooks-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['qbo-invoice-detail', qboInvoice.id] });
      toast.success(`Synced ${qboField} to QuickBooks`);
    } catch (e) {
      toast.error(`Failed to sync to QBO`, { description: (e as Error).message });
    }
  };

  // Documents
  const documentsQuery = useInvoiceDocuments(invoiceId || '');
  const addDocMutation = useAddInvoiceDocument();
  const removeDocMutation = useRemoveInvoiceDocument();
  const documents = invoiceId ? (documentsQuery.data || []) : [];
  const linkedDocIds = new Set(documents.map((d) => d.documentId));

  // Activities (engagement / audit timeline)
  const activitiesQuery = useInvoiceActivities(invoiceId || '');
  const addActivityMutation = useAddInvoiceActivity();
  const activities = invoiceId ? (activitiesQuery.data?.activities || []) : [];
  const [noteDraft, setNoteDraft] = useState('');

  const [docMode, setDocMode] = useState<'list' | 'search'>('list');
  const [docSearch, setDocSearch] = useState('');
  const [docResults, setDocResults] = useState<Array<{ id: string; name: string }>>([]);
  const [isDocSearching, setIsDocSearching] = useState(false);

  const handleDocSearch = async (query: string) => {
    setDocSearch(query);
    if (!query.trim()) { setDocResults([]); return; }
    setIsDocSearching(true);
    try {
      const results = await searchDocuments(query);
      setDocResults(results.data || []);
    } catch { setDocResults([]); }
    finally { setIsDocSearching(false); }
  };

  // Push a DMS document to QBO as an attachment
  const pushDocToQbo = async (documentId: string) => {
    if (!qboInvoice?.id) {
      toast.info('No QBO invoice linked — document saved in ATEK only');
      return;
    }
    try {
      const [detail, urlResp] = await Promise.all([
        getDocumentDetail(documentId),
        getDocumentDownloadUrl(documentId),
      ]);
      const doc = detail.data;
      const fileName = doc?.name || `document-${documentId}`;
      const contentType = doc?.currentVersion?.sourceMimeType || 'application/octet-stream';
      await api.post(`/v4/quickbooks/${qboInvoice.id}/attach-document`, {
        downloadUrl: urlResp.downloadUrl,
        fileName,
        contentType,
      });
      toast.success(`${fileName} attached to QBO`);
    } catch (e) {
      toast.warning('Linked in ATEK but QBO attachment failed', { description: (e as Error).message });
    }
  };

  const handleDocUploaded = async (documentId: string) => {
    if (!invoiceId) return;
    await addDocMutation.mutateAsync({ invoiceId, documentId });
    queryClient.invalidateQueries({ queryKey: ['atek-invoices-sync'] });
    queryClient.invalidateQueries({ queryKey: ['atek-invoices'] });
    await pushDocToQbo(documentId);
  };

  const handleDocLink = async (documentId: string) => {
    if (!invoiceId) return;
    await addDocMutation.mutateAsync({ invoiceId, documentId });
    queryClient.invalidateQueries({ queryKey: ['atek-invoices-sync'] });
    queryClient.invalidateQueries({ queryKey: ['atek-invoices'] });
    await pushDocToQbo(documentId);
    setDocSearch('');
    setDocResults([]);
    setDocMode('list');
  };

  // Refresh — re-pull the latest data from every backend (Mongo, V4 PG, QBO,
  // DMS, activity timeline) without closing the sheet. Useful after running
  // the bookkeeper, after accounts_sync ticks, or just to confirm a cross-
  // system mutation landed everywhere. Invalidates with permissive predicates
  // so we don't have to enumerate every internal query key.
  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // 1. Bust the SERVER-side QB cache (PG `qb_cache` table). Without this,
      //    /v4/quickbooks/:id keeps serving the 30-min-stale view even after
      //    we invalidate frontend caches. The `?refresh=1` query param tells
      //    the route to cacheDel before fetching from the gateway.
      if (qboInvoice?.id) {
        try { await api.get(`/v4/quickbooks/${qboInvoice.id}`, { refresh: '1' }); }
        catch { /* non-fatal — frontend invalidation below will retry */ }
      }
      // 2. Invalidate frontend TanStack Query caches.
      await Promise.all([
        queryClient.invalidateQueries({ predicate: (q) => {
          const k = q.queryKey?.[0];
          return typeof k === 'string' && (
            k === 'atek-invoice-detail'
            || k === 'atek-invoices-sync'
            || k === 'atek-invoices'
            || k === 'atek-invoices-sync-map'
            || k === 'quickbooks-invoices'
            || k === 'qbo-invoice-detail'
            || k === 'quickbooks-invoices-all-numbers'
            || k === 'invoices'  // TanStack invoiceKeys.* (documents, activities, detail)
            || k === 'documents' // documentKeys.* (detail/name, preview-url) — slot keyword match reads doc.name
          );
        } }),
        // AI Memory tab — pull the latest reconciliation thread working memory too.
        queryClient.invalidateQueries({ queryKey: ['aging', 'invoice-memory', invoiceNumber] }),
        // History tab — refresh the AR engine reconciliation timeline.
        queryClient.invalidateQueries({ queryKey: ['aging', 'invoice-ar-evidence', invoiceNumber] }),
      ]);
      toast.success(`Refreshed invoice ${invoiceNumber}`);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-6xl overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <SheetTitle>Invoice Comparison — {invoiceNumber}</SheetTitle>
              {atekV4?.arDoneAt ? (
                <Badge variant="success" title={`AR Done — stamped ${new Date(atekV4.arDoneAt).toLocaleString('en-CA')}`}>
                  ✅ AR Done
                </Badge>
              ) : (
                <Badge variant="secondary" title="AR work not yet complete — see Reference Documents and validate">
                  ⏳ AR Pending
                </Badge>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              className="mr-8"
              title="Re-fetch latest data from Mongo, V4 PG, QBO, DMS, activity timeline"
            >
              {refreshing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
              Refresh
            </Button>
          </div>
        </SheetHeader>

        <Tabs value={sheetTab} onValueChange={(v) => setSheetTab(v as 'details' | 'memory' | 'history')} className="mt-3">
          <TabsList className="h-8 mb-3">
            <TabsTrigger value="details" className="text-xs px-3">Details</TabsTrigger>
            <TabsTrigger value="memory" className="text-xs px-3">Memory</TabsTrigger>
            <TabsTrigger value="history" className="text-xs px-3">History</TabsTrigger>
          </TabsList>
          <TabsContent value="details">
        {loading ? (
          <div className="space-y-3 mt-4">
            {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
          </div>
        ) : (
          <div className="mt-4 space-y-6">
            {/* Header fields */}
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-32">Field</TableHead>
                    <TableHead>
                      <span className="flex items-center gap-1.5">
                        Mongo (V3)
                        {mongoInvoiceId && (
                          <a
                            href={`/app/invoices/${mongoInvoiceId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-muted-foreground hover:text-primary"
                            title="Open in legacy ATEK (MongoDB)"
                          >
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </span>
                    </TableHead>
                    <TableHead>
                      <span className="flex items-center gap-1.5 text-blue-700">
                        Postgres (V4)
                      </span>
                    </TableHead>
                    <TableHead>
                      <span className="flex items-center gap-1.5">
                        QuickBooks
                        {qboInvoice?.id && (
                          <a
                            href={`https://app.qbo.intuit.com/app/invoice?txnId=${qboInvoice.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-muted-foreground hover:text-primary"
                            title="Open in QuickBooks Online"
                          >
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </span>
                    </TableHead>
                    <TableHead className="w-24 text-xs">Actions</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <ComparisonRow label="Invoice #" mongoValue={atekInvoice?.invoice_number || ''} v4Value={atekV4?.invoiceNumber || ''} qboValue={qboInvoice?.docNumber || ''} />
                  <ComparisonRow
                    label="Status"
                    mongoValue={atekInvoice?.status || ''}
                    v4Value={atekV4?.status || ''}
                    qboValue={qboInvoice?.status || ''}
                    matchOverride={!!(atekInvoice?.status && qboInvoice?.status && (String(atekInvoice.status) === String(qboInvoice.status) || STATUS_EQUIVALENTS[atekInvoice.status] === qboInvoice.status))}
                    action={!qboInvoice && atekInvoice ? (
                      <Button
                        size="sm"
                        className="h-6 text-xs px-2"
                        disabled={creatingInQbo}
                        onClick={async () => {
                          if (!window.confirm(`Create invoice #${atekInvoice.invoice_number} in QuickBooks?`)) return;
                          setCreatingInQbo(true);
                          try {
                            toast.info('Creating in QuickBooks...', { id: 'create-qbo' });
                            const customerName = typeof atekInvoice.customer === 'object' ? atekInvoice.customer.name : '';
                            // Need a QBO customer ID — try to find by name
                            const custRes = await api.get<{ customers: Array<{ id: string; name: string }> }>('/v4/quickbooks/customers/list');
                            const customers = custRes.customers || [];
                            const stripPrefix = (s: string) => s.replace(/^\d[\d-]*\s+/, '');
                            const match = customers.find((c) => {
                              const a = stripPrefix(c.name.toLowerCase());
                              const b = stripPrefix((atekV4?.accountName || customerName).toLowerCase());
                              return a === b || c.name.toLowerCase().includes(b) || b.includes(a);
                            });
                            if (!match) {
                              toast.error('No matching QBO customer found. Assign the account first.', { id: 'create-qbo' });
                              return;
                            }
                            await api.post('/v4/quickbooks/create-from-atek', {
                              invoiceNumber: atekInvoice.invoice_number,
                              date: atekInvoice.date?.slice(0, 10),
                              dueDate: atekInvoice.expiration_date?.slice(0, 10),
                              customerId: match.id,
                              customerName: match.name,
                              total: atekInvoice.subtotal_amount ?? atekInvoice.total ?? 0,
                              currency: atekInvoice.currency || 'CAD',
                              paymentTerms: atekInvoice.payment_terms,
                              poNumber: atekInvoice.po_number,
                              taxCategory: atekInvoice.tax_category,
                              lines: (atekInvoice.skus || []).map((s) => ({
                                sku: s.sku,
                                description: s.description || s.sku,
                                quantity: s.quantity,
                                unitPrice: s.unit_price,
                                amount: s.total,
                              })),
                            });
                            queryClient.invalidateQueries({ queryKey: ['quickbooks-invoices'] });
                            queryClient.invalidateQueries({ queryKey: ['quickbooks-invoices-all-numbers'] });
                            queryClient.invalidateQueries({ queryKey: ['atek-invoices-sync-map'] });
                            toast.success('Invoice created in QuickBooks', { id: 'create-qbo' });
                          } catch (e) {
                            toast.error('Failed to create in QBO', { id: 'create-qbo', description: (e as Error).message });
                          } finally {
                            setCreatingInQbo(false);
                          }
                        }}
                      >
                        {creatingInQbo ? <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Creating...</> : 'Create in QBO'}
                      </Button>
                    ) : qboInvoice && qboInvoice.balance > 0 && atekInvoice?.status === 'cancelled' ? (
                      <Button
                        variant="destructive"
                        size="sm"
                        className="h-6 text-xs px-2"
                        disabled={voidingQbo}
                        onClick={async () => {
                          if (!window.confirm(`Void invoice #${qboInvoice.docNumber} in QuickBooks?\nThis is irreversible.`)) return;
                          setVoidingQbo(true);
                          try {
                            toast.info('Voiding in QuickBooks...', { id: 'void-qbo' });
                            await api.post(`/v4/quickbooks/${qboInvoice.id}/void`, {});
                            queryClient.invalidateQueries({ queryKey: ['quickbooks-invoices'] });
                            queryClient.invalidateQueries({ queryKey: ['quickbooks-invoices-all-numbers'] });
                            queryClient.invalidateQueries({ queryKey: ['qbo-invoice-detail', qboInvoice.id] });
                            toast.success('Invoice voided in QuickBooks', { id: 'void-qbo' });
                          } catch (e) {
                            toast.error('Failed to void', { id: 'void-qbo', description: (e as Error).message });
                          } finally {
                            setVoidingQbo(false);
                          }
                        }}
                      >
                        {voidingQbo ? <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Voiding...</> : 'Void in QBO'}
                      </Button>
                    ) : undefined}
                  />
                  <ComparisonRow label="Date" mongoValue={atekInvoice?.date ? formatDate(atekInvoice.date) : ''} v4Value={atekV4?.invoiceDate ? formatDate(atekV4.invoiceDate) : ''} qboValue={qboInvoice?.date ? formatDate(qboInvoice.date) : ''} />
                  {/* Payment Terms — editable on Mongo, V4 read-only */}
                  <TableRow>
                    <TableCell className="font-medium text-muted-foreground text-xs w-32">Payment Terms</TableCell>
                    <TableCell className="text-sm">
                      <Select
                        value={atekInvoice?.payment_terms || ''}
                        onValueChange={(v) => handleUpdateField('payment_terms', v)}
                        disabled={!mongoInvoiceId || updateInvoiceMutation.isPending}
                      >
                        <SelectTrigger className="h-7 text-xs w-32">
                          <SelectValue placeholder="—" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="on-order">On Order</SelectItem>
                          <SelectItem value="net-0">Net 0</SelectItem>
                          <SelectItem value="net-30">Net 30</SelectItem>
                          <SelectItem value="net-45">Net 45</SelectItem>
                          <SelectItem value="net-60">Net 60</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className={cn('text-sm whitespace-normal', atekInvoice?.payment_terms && atekV4?.paymentTerms && atekInvoice.payment_terms !== atekV4.paymentTerms && 'text-amber-700 font-medium')}>{atekV4?.paymentTerms || '—'}</TableCell>
                    <TableCell className="text-sm whitespace-normal">—</TableCell>
                    <TableCell className="text-xs" />
                    <TableCell className="w-10 text-center">
                      {atekInvoice?.payment_terms && (
                        <button type="button" onClick={() => handleSyncFieldToQbo('paymentTerms', atekInvoice.payment_terms!)} className="text-muted-foreground hover:text-blue-600 p-0.5" title="Sync to QuickBooks">
                          <ArrowRight className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                  <ComparisonRow
                    label="Due Date"
                    mongoValue={atekInvoice?.expiration_date ? formatDate(atekInvoice.expiration_date) : ''}
                    v4Value={atekV4?.dueDate ? formatDate(atekV4.dueDate) : ''}
                    qboValue={qboInvoice?.dueDate ? formatDate(qboInvoice.dueDate) : ''}
                    onSyncToQbo={(atekV4?.dueDate || atekInvoice?.expiration_date) ? () => handleSyncFieldToQbo('dueDate', atekV4?.dueDate || atekInvoice?.expiration_date || '') : undefined}
                  />
                  <ComparisonRow
                    label="Organisation"
                    mongoValue={atekInvoice?.customer && typeof atekInvoice.customer === 'object' ? atekInvoice.customer.name : String(atekInvoice?.customer || '')}
                    v4Value={''}
                    qboValue={''}
                  />
                  <AccountComparisonRow
                    mongoAccountName={atekInvoice?.customer && typeof atekInvoice.customer === 'object' ? atekInvoice.customer.name : ''}
                    v4AccountName={atekV4?.accountName || ''}
                    qboCustomerName={qboInvoice?.customerName || ''}
                    qboInvoiceId={qboInvoice?.id || null}
                    atekInvoicePgId={invoiceId}
                  />
                  <ComparisonRow
                    label="Sales Manager"
                    mongoValue={atekInvoice?.sales_manager && typeof atekInvoice.sales_manager === 'object' ? [atekInvoice.sales_manager.firstname, atekInvoice.sales_manager.lastname].filter(Boolean).join(' ') : ''}
                    v4Value={atekV4?.salesManagerName || ''}
                    qboValue={''}
                  />
                  <ComparisonRow
                    label="Contractual Mgr"
                    mongoValue={(() => {
                      if (!atekInvoice?.contractual_manager || typeof atekInvoice.contractual_manager !== 'object') return '';
                      const cm = atekInvoice.contractual_manager;
                      const name = [cm.firstname, cm.lastname].filter(Boolean).join(' ');
                      return cm.email ? `${name} <${cm.email}>` : name;
                    })()}
                    v4Value={(() => {
                      if (!atekV4?.contractualManagerName) return '';
                      return atekV4.contractualManagerEmail ? `${atekV4.contractualManagerName} <${atekV4.contractualManagerEmail}>` : atekV4.contractualManagerName;
                    })()}
                    qboValue={''}
                  />
                  {/* AP Email — extracted from the customer PO and stored on the
                      `companies.ap_contact_emails` array. Surfaced here so AR
                      knows where to route invoice copies / dunning notices.
                      V4 reads from the joined company; Mongo had no equivalent
                      column, so we leave it blank on the legacy side. */}
                  <ComparisonRow
                    label="AP Email"
                    mongoValue={''}
                    v4Value={(atekV4?.apContactEmails ?? []).filter(Boolean).join(', ')}
                    qboValue={''}
                  />
                  <ComparisonRow label="Currency" mongoValue={atekInvoice?.currency || ''} v4Value={atekV4?.currency || ''} qboValue={qboInvoice?.currency || ''} />
                  {(() => {
                    // Derive Mongo Subtotal/Tax/Total with fallbacks since legacy Mongo records
                    // often have `subtotal_amount`, `tax_amount`, and even `total` either null
                    // or storing a pre-tax-only number. Resolution order:
                    //   • Subtotal: subtotal_amount → SKUs sum → (total - tax_amount)
                    //   • Tax:      tax_amount > 0 → subtotal × rate(tax_category)
                    //               → (total - subtotal) when total clearly post-tax
                    //   • Total:    subtotal + tax  (recomputed; do NOT trust `atekInvoice.total`
                    //               which is unreliably pre- or post-tax across vintages)
                    // Rates come from @atek/ca-tax (the repo-wide source of truth); the
                    // extra cases below are legacy/QBO spellings that never existed as a
                    // stored tax_category.
                    const cur = atekInvoice?.currency || 'CAD';
                    const taxRateFromCategory = (cat?: string | null): number | null => {
                      if (!cat) return null;
                      const k = cat.toUpperCase().replace(/[\s-]/g, '');
                      if (k === 'HST15') return TAX_CATEGORIES.HST15.ratePct / 100; // 15% — NB/NS/NL/PE
                      switch (k) {
                        case 'GST+QST': case 'TPS+TVQ': return TAX_CATEGORIES['GST+QST'].ratePct / 100;
                        case 'GST+PST': return TAX_CATEGORIES['GST+PST'].ratePct / 100;
                        case 'HST': return TAX_CATEGORIES.HST.ratePct / 100; // 13% — ON
                        case 'GST': case 'TPS': return TAX_CATEGORIES.GST.ratePct / 100;
                        case 'PST': return 0.07;
                        case 'NONE': case 'EXEMPT': case 'ZERO': return 0;
                        default: return null;
                      }
                    };
                    // Sum lines POST-discount. `sku.discount` is a percentage (e.g. 20 = -20%).
                    // Without applying it, skusSum disagrees with invoice.total whenever any
                    // line carries a discount, breaking the Mongo/V4/QB drift detection below.
                    const skusSum = (atekInvoice?.skus ?? []).reduce(
                      (s, sku) => {
                        const disc = Number(sku.discount) || 0;
                        const gross = sku.quantity * sku.unit_price;
                        return s + (sku.total ?? gross * (1 - disc / 100));
                      },
                      0,
                    );
                    const hasSkus = (atekInvoice?.skus ?? []).length > 0;
                    const taxRate = taxRateFromCategory(atekInvoice?.tax_category);

                    const mongoSubtotalNum =
                      atekInvoice?.subtotal_amount != null
                        ? atekInvoice.subtotal_amount
                        : hasSkus
                        ? skusSum
                        : atekInvoice?.total != null && atekInvoice?.tax_amount != null
                        ? atekInvoice.total - atekInvoice.tax_amount
                        : null;

                    const mongoTaxNum = (() => {
                      // Prefer explicit positive tax_amount.
                      if (atekInvoice?.tax_amount != null && atekInvoice.tax_amount > 0) {
                        return atekInvoice.tax_amount;
                      }
                      // Else compute from tax_category × subtotal — handles legacy invoices
                      // where `total` was stored pre-tax (Tax row would otherwise be $0).
                      if (taxRate != null && mongoSubtotalNum != null) {
                        return Math.round(mongoSubtotalNum * taxRate * 100) / 100;
                      }
                      // Else, if total is clearly post-tax, derive from delta.
                      if (atekInvoice?.total != null && mongoSubtotalNum != null && atekInvoice.total > mongoSubtotalNum) {
                        return Math.max(atekInvoice.total - mongoSubtotalNum, 0);
                      }
                      // Last resort: explicit tax_amount even if zero (e.g. exempt).
                      return atekInvoice?.tax_amount ?? null;
                    })();

                    // Recompute total from parts. atekInvoice.total is unreliable across
                    // vintages: some records store pre-tax, some post-tax.
                    const mongoTotalNum =
                      mongoSubtotalNum != null && mongoTaxNum != null
                        ? Math.round((mongoSubtotalNum + mongoTaxNum) * 100) / 100
                        : atekInvoice?.total ?? null;

                    return (
                      <>
                        <ComparisonRow
                          label="Subtotal"
                          mongoValue={mongoSubtotalNum != null ? formatCurrency(mongoSubtotalNum, cur) : ''}
                          v4Value={atekV4 ? formatCurrency(parseFloat(atekV4.subtotal), atekV4.currency || 'CAD') : ''}
                          qboValue={qboInvoice ? formatCurrency(qboInvoice.total - (qboInvoice.taxDetail?.TotalTax ?? 0), qboInvoice.currency || 'CAD') : ''}
                        />
                        <ComparisonRow
                          label="Tax"
                          mongoValue={mongoTaxNum != null ? formatCurrency(mongoTaxNum, cur) : ''}
                          v4Value={atekV4 ? formatCurrency(parseFloat(atekV4.taxAmount), atekV4.currency || 'CAD') : ''}
                          qboValue={qboInvoice?.taxDetail?.TotalTax != null ? formatCurrency(qboInvoice.taxDetail.TotalTax, qboInvoice.currency || 'CAD') : ''}
                        />
                        <ComparisonRow
                          label="Total"
                          mongoValue={mongoTotalNum != null ? formatCurrency(mongoTotalNum, cur) : ''}
                          v4Value={atekV4 ? formatCurrency(parseFloat(atekV4.total), atekV4.currency || 'CAD') : ''}
                          qboValue={qboInvoice?.total != null ? formatCurrency(qboInvoice.total, qboInvoice.currency || 'CAD') : ''}
                        />
                        <ComparisonRow
                          label="Balance"
                          mongoValue={(() => {
                            if (atekInvoice?.status === 'paid' || atekInvoice?.status === 'cancelled') return formatCurrency(0, cur);
                            return mongoTotalNum != null ? formatCurrency(mongoTotalNum, cur) : '';
                          })()}
                          v4Value={(() => {
                            if (!atekV4) return '';
                            if (atekV4.status === 'paid' || atekV4.status === 'cancelled') return formatCurrency(0, atekV4.currency || 'CAD');
                            return formatCurrency(parseFloat(atekV4.total), atekV4.currency || 'CAD');
                          })()}
                          qboValue={qboInvoice?.balance != null ? formatCurrency(qboInvoice.balance, qboInvoice.currency || 'CAD') : ''}
                        />
                      </>
                    );
                  })()}
                  <ComparisonRow
                    label="Tax Category"
                    mongoValue={atekInvoice?.tax_category || ''}
                    v4Value={atekV4?.taxCategory || ''}
                    qboValue={(() => {
                      const lines = qboInvoice?.taxDetail?.TaxLine ?? [];
                      if (!lines || lines.length === 0) return '';
                      const rates = lines.map((l) => l.TaxLineDetail?.TaxPercent).filter((r): r is number => r != null);
                      const has5 = rates.some((r) => Math.abs(r - 5) < 0.1);
                      const has9975 = rates.some((r) => Math.abs(r - 9.975) < 0.1);
                      const has7 = rates.some((r) => Math.abs(r - 7) < 0.1);
                      const has13 = rates.some((r) => Math.abs(r - 13) < 0.1);
                      const has15 = rates.some((r) => Math.abs(r - 15) < 0.1);
                      if (has5 && has9975) return 'GST+QST';
                      if (has5 && has7) return 'GST+PST';
                      if (has13) return 'HST';
                      if (has15) return 'HST15';
                      if (has5) return 'GST';
                      return rates.map((r) => `${r}%`).join(' + ');
                    })()}
                  />
                  {/* Origin: Quote # — Mongo has direct quote_number, V4 stores quoteId only.
                      QBO has no quote concept. */}
                  <ComparisonRow
                    label="Quote #"
                    mongoValue={atekInvoice?.quote_number || ''}
                    v4Value={atekV4?.quoteId ? `quote ${atekV4.quoteId.slice(0, 8)}…` : ''}
                    qboValue=""
                  />
                  {/* Origin: Contract / Subscription # — Mongo populated; V4 has number directly. */}
                  <ComparisonRow
                    label="Contract #"
                    mongoValue={(() => {
                      const display = getSubscriptionDisplay(atekInvoice?.contract);
                      return display === '-' ? '' : display;
                    })()}
                    v4Value={atekV4?.contractNumber || atekV4?.subscriptionNumber || ''}
                    qboValue=""
                  />
                  {/* P/O Number — editable on Mongo, V4 read-only, sync arrow to QBO */}
                  <TableRow className={cn(atekInvoice?.po_number && atekV4?.poNumber && atekInvoice.po_number !== atekV4.poNumber && 'bg-red-50')}>
                    <TableCell className="font-medium text-muted-foreground text-xs w-32">P/O Number</TableCell>
                    <TableCell className="text-sm">
                      <Input
                        className="h-7 text-xs w-36"
                        defaultValue={atekInvoice?.po_number || ''}
                        placeholder="—"
                        disabled={!mongoInvoiceId || updateInvoiceMutation.isPending}
                        onBlur={(e) => {
                          const newVal = e.target.value;
                          const oldVal = atekInvoice?.po_number || '';
                          if (newVal !== oldVal) handleUpdateField('po_number', newVal || null);
                        }}
                      />
                    </TableCell>
                    <TableCell className={cn('text-sm whitespace-normal', atekInvoice?.po_number && atekV4?.poNumber && atekInvoice.po_number !== atekV4.poNumber && 'text-amber-700 font-medium')}>{atekV4?.poNumber || '—'}</TableCell>
                    <TableCell className="text-sm whitespace-normal">{qboInvoice?.poNumber || '—'}</TableCell>
                    <TableCell className="text-xs" />
                    <TableCell className="w-10 text-center">
                      {atekInvoice?.po_number && (
                        <button type="button" onClick={() => handleSyncFieldToQbo('poNumber', atekInvoice.po_number!)} className="text-muted-foreground hover:text-blue-600 p-0.5" title="Sync to QuickBooks">
                          <ArrowRight className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                  {/* Payment Status — editable select */}
                  <TableRow>
                    <TableCell className="font-medium text-muted-foreground text-xs w-32">Payment Status</TableCell>
                    <TableCell className="text-sm">
                      <Select
                        value={atekInvoice?.status || ''}
                        onValueChange={(v) => handleUpdateField('status', v)}
                        disabled={!mongoInvoiceId || updateInvoiceMutation.isPending}
                      >
                        <SelectTrigger className="h-7 text-xs w-32">
                          <SelectValue placeholder="—" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="preparation">Preparation</SelectItem>
                          <SelectItem value="sent">Sent</SelectItem>
                          <SelectItem value="paid">Paid</SelectItem>
                          <SelectItem value="expired">Expired</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className={cn('text-sm whitespace-normal', atekInvoice?.status && atekV4?.status && atekInvoice.status !== atekV4.status && 'text-amber-700 font-medium')}>{atekV4?.status || '—'}</TableCell>
                    <TableCell className="text-sm whitespace-normal">
                      {qboInvoice ? (qboInvoice.balance === 0 ? 'Paid' : qboInvoice.balance < qboInvoice.total ? 'Partial' : 'Unpaid') : '—'}
                    </TableCell>
                    <TableCell className="text-xs" />
                    <TableCell className="w-10 text-center">
                      {(() => {
                        const atekPaid = atekInvoice?.status === 'paid';
                        const qboPaid = qboInvoice?.balance === 0;
                        if (!atekInvoice || !qboInvoice) return null;
                        return atekPaid === qboPaid
                          ? <Check className="h-4 w-4 text-green-600 inline" />
                          : <X className="h-4 w-4 text-red-500 inline" />;
                      })()}
                    </TableCell>
                  </TableRow>
                  {/* Payment Date — editable on Mongo, V4 read-only */}
                  <TableRow>
                    <TableCell className="font-medium text-muted-foreground text-xs w-32">Payment Date</TableCell>
                    <TableCell className="text-sm">
                      <Input
                        type="date"
                        className="h-7 text-xs w-36"
                        defaultValue={atekInvoice?.payment_date?.slice(0, 10) || ''}
                        disabled={!mongoInvoiceId || updateInvoiceMutation.isPending}
                        onBlur={(e) => {
                          const newVal = e.target.value;
                          const oldVal = atekInvoice?.payment_date?.slice(0, 10) || '';
                          if (newVal !== oldVal) handleUpdateField('payment_date', newVal || null);
                        }}
                      />
                    </TableCell>
                    <TableCell className={cn('text-sm whitespace-normal', atekInvoice?.payment_date && atekV4?.paymentDate && atekInvoice.payment_date.slice(0, 10) !== atekV4.paymentDate.slice(0, 10) && 'text-amber-700 font-medium')}>{atekV4?.paymentDate ? formatDate(atekV4.paymentDate) : '—'}</TableCell>
                    <TableCell className="text-sm whitespace-normal">
                      {qboInvoice && qboInvoice.balance === 0 && qboInvoice.updatedAt ? (
                        <span title="QBO doesn't expose payment_date directly; this is the invoice's last-updated timestamp, which equals the payment date when the invoice was paid in full">
                          ≈ {formatDate(qboInvoice.updatedAt)}
                        </span>
                      ) : '—'}
                    </TableCell>
                    <TableCell className="text-xs" />
                    <TableCell className="w-10" />
                  </TableRow>
                  {/* Payment Ref — editable on Mongo, V4 read-only */}
                  <TableRow>
                    <TableCell className="font-medium text-muted-foreground text-xs w-32">Payment Ref</TableCell>
                    <TableCell className="text-sm">
                      <Input
                        className="h-7 text-xs w-36"
                        defaultValue={atekInvoice?.payment_ref || ''}
                        placeholder="—"
                        disabled={!mongoInvoiceId || updateInvoiceMutation.isPending}
                        onBlur={(e) => {
                          const newVal = e.target.value;
                          const oldVal = atekInvoice?.payment_ref || '';
                          if (newVal !== oldVal) handleUpdateField('payment_ref', newVal || null);
                        }}
                      />
                    </TableCell>
                    <TableCell className={cn('text-sm whitespace-normal', atekInvoice?.payment_ref && atekV4?.paymentRef && atekInvoice.payment_ref !== atekV4.paymentRef && 'text-amber-700 font-medium')}>{atekV4?.paymentRef || '—'}</TableCell>
                    <TableCell className="text-sm whitespace-normal">—</TableCell>
                    <TableCell className="text-xs" />
                    <TableCell className="w-10" />
                  </TableRow>
                  <ComparisonRow label="Memo" mongoValue={''} v4Value={''} qboValue={qboInvoice?.memo || ''} />
                </TableBody>
              </Table>
            </div>

            {/* Line items */}
            <div>
              <h3 className="text-sm font-semibold mb-2">Line Items</h3>
              <div className="grid grid-cols-2 gap-4">
                {/* ATEK SKUs.
                    `sku.discount` from Mongo is a PERCENTAGE (e.g. 20 = -20%, 100 = free).
                    Net per line = qty × unit_price × (1 − discount/100). The legacy V3
                    invoice shape doesn't populate `sku.total`, so we always recompute net
                    from qty/price/discount. Without this, the line totals don't match the
                    invoice total (which IS post-discount). */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-muted/50 px-3 py-1.5 text-xs font-semibold text-muted-foreground">ATEK</div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">SKU</TableHead>
                        <TableHead className="text-xs text-right">Qty</TableHead>
                        <TableHead className="text-xs text-right">Price</TableHead>
                        <TableHead className="text-xs text-right">Disc.</TableHead>
                        <TableHead className="text-xs text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(atekInvoice?.skus ?? []).length === 0 ? (
                        <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground text-xs py-4">No line items</TableCell></TableRow>
                      ) : (
                        (atekInvoice?.skus ?? []).map((sku, i) => {
                          const disc = Number(sku.discount) || 0;
                          const gross = sku.quantity * sku.unit_price;
                          // Prefer explicit sku.total when populated (rare in V3); else compute net.
                          const net = sku.total ?? (gross * (1 - disc / 100));
                          return (
                            <TableRow key={i}>
                              <TableCell className="text-xs">{sku.sku}{sku.description ? <span className="block text-muted-foreground truncate max-w-[150px]">{sku.description}</span> : null}</TableCell>
                              <TableCell className="text-xs text-right tabular-nums">{sku.quantity}</TableCell>
                              <TableCell className="text-xs text-right tabular-nums">{formatCurrency(sku.unit_price)}</TableCell>
                              <TableCell className="text-xs text-right tabular-nums text-muted-foreground">
                                {disc > 0 ? `−${disc.toFixed(disc % 1 === 0 ? 0 : 2)}%` : '—'}
                              </TableCell>
                              <TableCell className="text-xs text-right tabular-nums">
                                {disc > 0 ? (
                                  <span title={`Gross: ${formatCurrency(gross)} − ${disc}% discount`}>
                                    <span className="text-muted-foreground line-through mr-1">{formatCurrency(gross)}</span>
                                    {formatCurrency(net)}
                                  </span>
                                ) : (
                                  formatCurrency(net)
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                      {(atekInvoice?.skus ?? []).length > 0 && (
                        <TableRow className="bg-muted/30 font-semibold">
                          <TableCell className="text-xs" colSpan={3}>Total (post-discount)</TableCell>
                          <TableCell className="text-xs text-right tabular-nums" />
                          <TableCell className="text-xs text-right tabular-nums">
                            {formatCurrency((atekInvoice?.skus ?? []).reduce((s, sku) => {
                              const disc = Number(sku.discount) || 0;
                              const gross = sku.quantity * sku.unit_price;
                              return s + (sku.total ?? (gross * (1 - disc / 100)));
                            }, 0))}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* QBO Lines */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-muted/50 px-3 py-1.5 text-xs font-semibold text-muted-foreground">QuickBooks</div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">SKU</TableHead>
                        <TableHead className="text-xs text-right">Qty</TableHead>
                        <TableHead className="text-xs text-right">Price</TableHead>
                        <TableHead className="text-xs text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(qboInvoice?.lines ?? []).length === 0 ? (
                        <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground text-xs py-4">No line items</TableCell></TableRow>
                      ) : (
                        (qboInvoice?.lines ?? []).map((line) => (
                          <TableRow key={line.id}>
                            <TableCell className="text-xs">{line.sku || line.itemName}{line.sku && line.itemName ? <span className="block text-muted-foreground truncate max-w-[150px]">{line.itemName}</span> : line.description ? <span className="block text-muted-foreground truncate max-w-[150px]">{line.description.split('\n')[0]}</span> : null}</TableCell>
                            <TableCell className="text-xs text-right tabular-nums">{line.quantity}</TableCell>
                            <TableCell className="text-xs text-right tabular-nums">{formatCurrency(line.unitPrice)}</TableCell>
                            <TableCell className="text-xs text-right tabular-nums">{formatCurrency(line.amount)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>

            {/* Reference Documents — typed slots for the canonical invoice paper trail.
                All slots store their files in the DMS; the heuristic filters by document
                name keyword until the invoice schema gets typed FK columns (po_document_id,
                contract_document_id, invoice_pdf_document_id, payment_notification_document_id,
                bank_hit_document_id, quote_pdf_document_id). The "Attach" button scrolls to the catch-all
                Documents section below where the user can search/upload+link. */}
            <div>
              <h3 className="text-sm font-semibold mb-2">Reference Documents</h3>
              <p className="text-xs text-muted-foreground mb-2">
                Stored in DMS, surfaced here. Filter is by filename keyword — name your uploads accordingly.
              </p>
              <div className="border rounded-lg divide-y bg-card">
                <DocumentSlot
                  label="Quote PDF"
                  hint="Origin proposal"
                  keywords={['quote', 'soumission', 'proposition', 'proposal']}
                  documents={documents}
                  onUnlink={(docId) => removeDocMutation.mutate({ invoiceId: invoiceId!, documentId: docId })}
                  isUnlinking={removeDocMutation.isPending}
                  onAttach={() => document.getElementById('dms-documents-section')?.scrollIntoView({ behavior: 'smooth' })}
                />
                <DocumentSlot
                  label="PO Document"
                  hint="Customer's purchase order (or fed-govt contract amendment)"
                  keywords={['po', 'purchase order', 'bon de commande', 'bdc', 'amendment', 'w6369']}
                  documents={documents}
                  onUnlink={(docId) => removeDocMutation.mutate({ invoiceId: invoiceId!, documentId: docId })}
                  isUnlinking={removeDocMutation.isPending}
                  onAttach={() => document.getElementById('dms-documents-section')?.scrollIntoView({ behavior: 'smooth' })}
                />
                <DocumentSlot
                  label="Contract"
                  hint="Supporting agreement"
                  keywords={['contract', 'contrat', 'agreement', 'msa', 'sow']}
                  documents={documents}
                  onUnlink={(docId) => removeDocMutation.mutate({ invoiceId: invoiceId!, documentId: docId })}
                  isUnlinking={removeDocMutation.isPending}
                  onAttach={() => document.getElementById('dms-documents-section')?.scrollIntoView({ behavior: 'smooth' })}
                />
                <DocumentSlot
                  label="Invoice PDF"
                  hint="Sent copy"
                  keywords={['invoice', 'facture']}
                  documents={documents}
                  onUnlink={(docId) => removeDocMutation.mutate({ invoiceId: invoiceId!, documentId: docId })}
                  isUnlinking={removeDocMutation.isPending}
                  onAttach={() => document.getElementById('dms-documents-section')?.scrollIntoView({ behavior: 'smooth' })}
                />
                <DocumentSlot
                  label="Payment Notification"
                  hint="Customer-side proof: email, cheque image, remittance advice"
                  keywords={['avis de paiement', 'avis paiement', 'payment advice', 'payment notification', 'remittance', 'eft notification', 'direct deposit', 'dépôt direct', 'cheque', 'chèque', 'check ', 'wire transfer', 'virement', 'bordereau']}
                  documents={documents}
                  onUnlink={(docId) => removeDocMutation.mutate({ invoiceId: invoiceId!, documentId: docId })}
                  isUnlinking={removeDocMutation.isPending}
                  onAttach={() => document.getElementById('dms-documents-section')?.scrollIntoView({ behavior: 'smooth' })}
                />
                <DocumentSlot
                  label="Bank Hit"
                  hint="Funds visible in our bank account (RBC alert / statement line)"
                  keywords={['rbc', 'deposit notice', 'avis de dépôt', 'deposit alert', 'bank hit', 'royal bank', 'bank statement', 'relevé bancaire']}
                  documents={documents}
                  onUnlink={(docId) => removeDocMutation.mutate({ invoiceId: invoiceId!, documentId: docId })}
                  isUnlinking={removeDocMutation.isPending}
                  onAttach={() => document.getElementById('dms-documents-section')?.scrollIntoView({ behavior: 'smooth' })}
                />
              </div>
            </div>

            {/* Documents — catch-all DMS link/upload section. Anchor target for the
                Reference Documents "Attach" buttons above. */}
            {invoiceId && (
              <div id="dms-documents-section">
                <h3 className="text-sm font-semibold mb-2">All Documents</h3>
                <div className="space-y-3">
                  <UploadDropzone onUploaded={handleDocUploaded} />

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => setDocMode(docMode === 'search' ? 'list' : 'search')}
                  >
                    <SearchIcon className="h-4 w-4 mr-1" />
                    {docMode === 'search' ? 'Cancel search' : 'Pick from DMS'}
                  </Button>

                  {docMode === 'search' && (
                    <div className="space-y-2">
                      <Input
                        placeholder="Search documents in DMS..."
                        value={docSearch}
                        onChange={(e) => handleDocSearch(e.target.value)}
                        autoFocus
                      />
                      {isDocSearching && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                          <Loader2 className="h-4 w-4 animate-spin" /> Searching...
                        </div>
                      )}
                      {docResults.length > 0 && (
                        <div className="space-y-1 max-h-48 overflow-y-auto border rounded p-2">
                          {docResults.map((doc) => (
                            <button
                              key={doc.id}
                              type="button"
                              onClick={() => handleDocLink(doc.id)}
                              disabled={linkedDocIds.has(doc.id) || addDocMutation.isPending}
                              className="w-full text-left flex items-center justify-between p-2 rounded hover:bg-muted text-sm disabled:opacity-50"
                            >
                              <span className="truncate min-w-0 flex-1">{doc.name}</span>
                              {linkedDocIds.has(doc.id) ? (
                                <span className="text-xs text-muted-foreground ml-2">Linked</span>
                              ) : (
                                <Plus className="h-4 w-4 ml-2 shrink-0 text-muted-foreground" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                      {docSearch && !isDocSearching && docResults.length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-2">No documents found</p>
                      )}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Linked Documents ({documents.length})</Label>
                    {documentsQuery.isLoading ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground py-4 justify-center">
                        <Loader2 className="h-4 w-4 animate-spin" /> Loading...
                      </div>
                    ) : documents.length === 0 ? (
                      <div className="text-center text-muted-foreground py-4 text-sm border rounded-lg">
                        No documents linked yet
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {documents.map((doc) => (
                          <DocRow
                            key={doc.id}
                            documentId={doc.documentId}
                            onUnlink={() => invoiceId && removeDocMutation.mutate({ invoiceId, documentId: doc.documentId })}
                            isUnlinking={removeDocMutation.isPending}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Activity Timeline — engagement + change events for this invoice.
                Backed by /v4/invoices/:id/activities (filters account_activities
                on related_entity_type='invoice'). Server-emitted events for
                business actions (reassign, status change, document linked) are
                interleaved with manual notes added below. Lives at the bottom
                because it's append-only and scroll-naturally-grows over time. */}
            {invoiceId && (
              <div>
                <h3 className="text-sm font-semibold mb-2">Activity Timeline</h3>
                <p className="text-xs text-muted-foreground mb-2">
                  Engagement, change, and note events for this invoice. Notes are visible to all ATEK users.
                </p>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add a note (e.g. 'Called Marie at AP, asked to resend PO')"
                      value={noteDraft}
                      onChange={(e) => setNoteDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey && noteDraft.trim()) {
                          e.preventDefault();
                          addActivityMutation.mutate({
                            invoiceId,
                            activityType: 'note_added',
                            description: noteDraft.trim(),
                          }, { onSuccess: () => setNoteDraft('') });
                        }
                      }}
                    />
                    <Button
                      type="button"
                      size="sm"
                      disabled={!noteDraft.trim() || addActivityMutation.isPending}
                      onClick={() => addActivityMutation.mutate({
                        invoiceId,
                        activityType: 'note_added',
                        description: noteDraft.trim(),
                      }, { onSuccess: () => setNoteDraft('') })}
                    >
                      {addActivityMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add'}
                    </Button>
                  </div>
                  {activitiesQuery.isLoading ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground py-4 justify-center">
                      <Loader2 className="h-4 w-4 animate-spin" /> Loading activity…
                    </div>
                  ) : activities.length === 0 ? (
                    <div className="text-center text-muted-foreground py-4 text-sm border rounded-lg">
                      No activity recorded yet
                    </div>
                  ) : (
                    <div className="border rounded-lg divide-y bg-card">
                      {activities.map((act) => (
                        <div key={act.id} className="px-3 py-2 text-xs flex items-start gap-3">
                          <Badge variant="secondary" className="text-[10px] shrink-0">
                            {act.activityType}
                          </Badge>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm">{act.description}</div>
                            <div className="text-muted-foreground mt-0.5">
                              {act.performedByName ? `${act.performedByName} · ` : ''}
                              {new Date(act.createdAt).toLocaleString('en-CA', { timeZone: 'America/Montreal' })}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
          </TabsContent>
          <TabsContent value="memory">
            <InvoiceMemoryTab invoiceNumber={invoiceNumber} enabled={open && sheetTab === 'memory'} />
          </TabsContent>
          <TabsContent value="history">
            <InvoiceHistoryTab invoiceNumber={invoiceNumber} enabled={open && sheetTab === 'history'} />
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

function SyncTab({ syncMap, qboInvoiceNumbers }: { syncMap: Array<{ invoiceNumber: string; quickbooksInvoiceId: string | null }>; qboInvoiceNumbers: Set<string> }) {
  const saved = useMemo(() => loadPersistedFilters().sync || {}, []);
  // Multi-selects are persisted via an effect below (not setter-wrapped) because
  // call sites use `setSelectedXxx(prev => ...)` functional updates.
  const savedAccounts = useMemo<string[]>(
    // Migrate legacy single-`account` field to the new `accounts` array.
    () => (saved.accounts && Array.isArray(saved.accounts)
      ? saved.accounts
      : saved.account
        ? [saved.account]
        : []),
    [saved],
  );
  const savedStatuses = useMemo<InvoiceStatus[]>(
    () => (Array.isArray(saved.statuses) ? saved.statuses as InvoiceStatus[] : []),
    [saved],
  );
  const savedAMs = useMemo<string[]>(
    () => (Array.isArray(saved.ams) ? saved.ams : []),
    [saved],
  );

  const [syncFilter, setSyncFilterRaw] = useState<SyncFilter>((saved.syncFilter as SyncFilter) || 'all');
  const [invoiceNumberFilter, setInvoiceNumberFilterRaw] = useState(saved.invoiceNumber || '');
  const [debouncedInvoiceNumber, setDebouncedInvoiceNumber] = useState(saved.invoiceNumber || '');
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>(savedAccounts);
  const [from, setFromRaw] = useState(saved.from || '');
  const [to, setToRaw] = useState(saved.to || '');
  const [sortKey, setSortKeyRaw] = useState<SortKey>((saved.sortKey as SortKey) || 'date');
  const [sortDir, setSortDirRaw] = useState<1 | -1>((saved.sortDir as 1 | -1) || -1);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSizeRaw] = useState(saved.pageSize || 25);

  const [dueDateFrom, setDueDateFromRaw] = useState(saved.dueDateFrom || '');
  const [dueDateTo, setDueDateToRaw] = useState(saved.dueDateTo || '');
  const [selectedStatuses, setSelectedStatuses] = useState<InvoiceStatus[]>(savedStatuses);
  const [selectedAMs, setSelectedAMs] = useState<string[]>(savedAMs);

  const persist = useCallback((patch: Partial<PersistedFilters['sync']>) => savePersistedFilters({ sync: patch }), []);
  const setSyncFilter = (v: SyncFilter) => { setSyncFilterRaw(v); persist({ syncFilter: v }); };
  const setInvoiceNumberFilter = (v: string) => { setInvoiceNumberFilterRaw(v); persist({ invoiceNumber: v }); };
  const setFrom = (v: string) => { setFromRaw(v); persist({ from: v }); };
  const setTo = (v: string) => { setToRaw(v); persist({ to: v }); };
  const setDueDateFrom = (v: string) => { setDueDateFromRaw(v); persist({ dueDateFrom: v }); };
  const setDueDateTo = (v: string) => { setDueDateToRaw(v); persist({ dueDateTo: v }); };
  const setSortKey = (v: SortKey) => { setSortKeyRaw(v); persist({ sortKey: v }); };
  const setSortDir = (v: 1 | -1) => { setSortDirRaw(v); persist({ sortDir: v }); };
  const setPageSize = (v: number) => { setPageSizeRaw(v); persist({ pageSize: v }); };

  // Persist multi-select arrays via effect — call sites use `(prev) => ...`
  // functional updates so wrapping the setter would change their signature.
  useEffect(() => { persist({ accounts: selectedAccounts }); }, [selectedAccounts, persist]);
  useEffect(() => { persist({ statuses: selectedStatuses }); }, [selectedStatuses, persist]);
  useEffect(() => { persist({ ams: selectedAMs }); }, [selectedAMs, persist]);

  // Selected invoice for comparison — kept in sync with the URL param so
  // /acc/invoices/<number> deep-links to the open sheet, and closing the
  // sheet returns to /acc/invoices. Single source of truth = URL.
  const { invoiceNumber: urlInvoiceNumber } = useParams<{ invoiceNumber?: string }>();
  const navigate = useNavigate();
  const selectedInvoiceNumber = urlInvoiceNumber ?? null;
  const setSelectedInvoiceNumber = useCallback((next: string | null) => {
    if (next) navigate(`/acc/invoices/${encodeURIComponent(next)}`);
    else navigate('/acc/invoices');
  }, [navigate]);

  useEffect(() => {
    const timer = setTimeout(() => { setDebouncedInvoiceNumber(invoiceNumberFilter); setPage(0); }, 300);
    return () => clearTimeout(timer);
  }, [invoiceNumberFilter]);


  // Build a map: invoiceNumber → quickbooksInvoiceId
  const qboIdByInvoiceNumber = useMemo(() => {
    const m = new Map<string, string>();
    for (const row of syncMap) {
      if (row.quickbooksInvoiceId) m.set(row.invoiceNumber, row.quickbooksInvoiceId);
    }
    return m;
  }, [syncMap]);

  // Fetch ATEK invoices (server-side paginated)
  const queryParams: Record<string, string | number> = {
    pageNumber: page,
    pageLength: pageSize,
    sortKey: ATEK_SORT_MAP[sortKey] || 'createdAt',
    sortDirection: sortDir,
  };
  if (debouncedInvoiceNumber) queryParams.invoiceNumberSearch = debouncedInvoiceNumber;
  if (from) queryParams.dateFrom = from;
  if (to) queryParams.dateTo = to;
  if (dueDateFrom) queryParams.expirationDateFrom = dueDateFrom;
  if (dueDateTo) queryParams.expirationDateTo = dueDateTo;
  if (selectedStatuses.length > 0) queryParams.status_in = JSON.stringify(selectedStatuses);

  const { data, isLoading, error } = useQuery({
    queryKey: ['atek-invoices-sync', queryParams],
    queryFn: () => api.get<V4InvoicesResponse>('/v4/invoices', queryParams),
    staleTime: 0,
  });

  const handleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir(sortDir === 1 ? -1 : 1);
    else { setSortKey(key); setSortDir(-1); }
    setPage(0);
  };

  const today = new Date().toISOString().slice(0, 10);
  const invoices = useMemo<NormalizedInvoice[]>(() => {
    let result = (data?.invoices ?? []).map((i) => {
      const totalNum = parseFloat(i.total);
      const isPaid = i.status === 'paid';
      const isCancelled = i.status === 'cancelled';
      const balance = isPaid || isCancelled ? 0 : totalNum;
      const overdue = i.status === 'sent' && !!i.dueDate && i.dueDate < today;
      const synced = !!i.quickbooksInvoiceId || qboInvoiceNumbers.has(i.invoiceNumber);
      return {
        id: i.id,
        invoiceNumber: i.invoiceNumber,
        date: i.invoiceDate || '',
        dueDate: i.dueDate || i.expirationDate || '',
        account: i.accountName || '',
        total: totalNum,
        balance,
        status: i.status,
        overdue,
        currency: i.currency || 'CAD',
        synced,
        documentCount: i.documentCount ?? 0,
        accountManager: i.salesManagerName || '',
      };
    });
    if (syncFilter === 'synced') result = result.filter((i) => i.synced);
    else if (syncFilter === 'unsynced') result = result.filter((i) => !i.synced);
    return result;
  }, [data, today, qboInvoiceNumbers, syncFilter, selectedAccounts, selectedAMs]);

  // Unique account/AM names from current page for multi-select options
  const uniqueAccounts = useMemo(() => {
    const names = new Set((data?.invoices ?? []).map((i) => i.accountName || '').filter(Boolean));
    return [...names].sort();
  }, [data]);
  const uniqueAMs = useMemo(() => {
    const names = new Set((data?.invoices ?? []).map((i) => i.salesManagerName || '').filter(Boolean));
    return [...names].sort();
  }, [data]);

  const total = syncFilter === 'all' ? (data?.total ?? 0) : invoices.length;
  const totalPages = Math.ceil(total / pageSize) || 1;
  const colCount = 10;

  // Fetch ATEK full invoice when selected (V3 for SKUs)
  const selectedQboId = selectedInvoiceNumber ? qboIdByInvoiceNumber.get(selectedInvoiceNumber) : null;
  // For the ATEK side, we need the MongoDB invoice (V3) for SKUs
  // First get the V4 invoice to find mongoInvoiceId
  const selectedV4 = useMemo(() => {
    return (data?.invoices ?? []).find((i) => i.invoiceNumber === selectedInvoiceNumber) ?? null;
  }, [data, selectedInvoiceNumber]);

  // 5-minute client cache matches the project-wide staleTime default
  // (apps/frontend-react/CLAUDE.md). The `qboAllQuery` below fetches the full
  // QBO invoice list (~1k rows) on first open of the ComparisonSheet — with
  // staleTime:0 every re-open refetched, which is why the panel felt blank.
  const SHEET_STALE_MS = 5 * 60 * 1000;

  const atekDetailQuery = useQuery({
    queryKey: ['atek-invoice-detail', selectedV4?.mongoInvoiceId],
    queryFn: () => api.get<{ invoice: Invoice }>(`/v4/legacy/invoices/${selectedV4!.mongoInvoiceId}`, {
      populate: JSON.stringify(['customer', 'contract', 'sales_manager', 'contractual_manager']),
    }),
    enabled: !!selectedV4?.mongoInvoiceId,
    staleTime: SHEET_STALE_MS,
  });

  // Find the QBO invoice ID to fetch — either from quickbooksInvoiceId or by searching cached list
  // If no quickbooksInvoiceId but invoice number matches, we need to find the QBO Id from the cached list
  const qboIdFromCache = useMemo(() => {
    if (selectedQboId) return selectedQboId;
    // Can't easily get QBO Id from docNumber without the full cached data
    // We'll need to look it up from the QBO all-invoices query
    return null;
  }, [selectedQboId]);

  const qboDetailQuery = useQuery({
    queryKey: ['qbo-invoice-detail', qboIdFromCache],
    queryFn: () => api.get<QboInvoiceDetail>(`/v4/quickbooks/${qboIdFromCache}`),
    enabled: !!qboIdFromCache,
    staleTime: SHEET_STALE_MS,
  });

  // If no direct QBO ID, try finding by docNumber from QBO all data.
  // Cache the heavy list so the 2nd+ sheet open is instant, and fetch
  // eagerly on tab mount so even the FIRST sheet open is warm.
  const qboAllQuery = useQuery({
    queryKey: ['quickbooks-invoices-all-for-sync'],
    queryFn: () => api.get<{ invoices: Array<{ id: string; docNumber: string }> }>('/v4/quickbooks'),
    staleTime: SHEET_STALE_MS,
    enabled: true,
  });

  const qboIdByDocNumber = useMemo(() => {
    const m = new Map<string, string>();
    for (const inv of qboAllQuery.data?.invoices ?? []) {
      m.set(inv.docNumber, inv.id);
    }
    return m;
  }, [qboAllQuery.data]);

  const fallbackQboId = selectedInvoiceNumber ? qboIdByDocNumber.get(selectedInvoiceNumber) : null;

  const qboFallbackDetailQuery = useQuery({
    queryKey: ['qbo-invoice-detail', fallbackQboId],
    queryFn: () => api.get<QboInvoiceDetail>(`/v4/quickbooks/${fallbackQboId}`),
    enabled: !!fallbackQboId && !qboIdFromCache,
    staleTime: SHEET_STALE_MS,
  });

  const qboDetail = qboDetailQuery.data || qboFallbackDetailQuery.data || null;
  const isLoadingQbo = (!!qboIdFromCache && qboDetailQuery.isLoading) || (!!fallbackQboId && !qboIdFromCache && qboFallbackDetailQuery.isLoading) || (!!selectedInvoiceNumber && !qboIdFromCache && !fallbackQboId && qboAllQuery.isLoading);

  return (
    <div className="space-y-4">
      {/* Table with inline column filters */}
      <div className="border rounded-lg overflow-hidden bg-background">
        <Table>
          <TableHeader>
            {/* ROW 1: Inline filters */}
            <TableRow className="bg-muted/30 hover:bg-muted/30">
              {/* Sync filter */}
              <TableHead className="w-10 p-1">
                <Select value={syncFilter} onValueChange={(v) => { setSyncFilter(v as SyncFilter); setPage(0); }}>
                  <SelectTrigger className="h-7 w-full text-xs px-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="synced">Synced</SelectItem>
                    <SelectItem value="unsynced">No</SelectItem>
                  </SelectContent>
                </Select>
              </TableHead>
              {/* Invoice # filter */}
              <TableHead className="p-1">
                <Input className="h-7 text-xs" value={invoiceNumberFilter} onChange={(e) => setInvoiceNumberFilter(e.target.value)} placeholder="..." />
              </TableHead>
              {/* Date filter (from/to) */}
              <TableHead className="p-1">
                <div className="flex flex-col gap-0.5">
                  <Input type="date" className="h-6 text-xs px-1" value={from} onChange={(e) => { setFrom(e.target.value); setPage(0); }} title="From" />
                  <Input type="date" className="h-6 text-xs px-1" value={to} onChange={(e) => { setTo(e.target.value); setPage(0); }} title="To" />
                </div>
              </TableHead>
              {/* Due Date filter (from/to) */}
              <TableHead className="p-1">
                <div className="flex flex-col gap-0.5">
                  <Input type="date" className="h-6 text-xs px-1" value={dueDateFrom} onChange={(e) => { setDueDateFrom(e.target.value); setPage(0); }} title="From" />
                  <Input type="date" className="h-6 text-xs px-1" value={dueDateTo} onChange={(e) => { setDueDateTo(e.target.value); setPage(0); }} title="To" />
                </div>
              </TableHead>
              {/* Account filter (multi-select) */}
              <TableHead className="p-1">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="h-7 w-full text-xs justify-start font-normal">
                      {selectedAccounts.length === 0 ? '—' : `${selectedAccounts.length} selected`}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56 max-h-64 overflow-y-auto">
                    {uniqueAccounts.map((name) => (
                      <DropdownMenuCheckboxItem
                        key={name}
                        checked={selectedAccounts.includes(name)}
                        onCheckedChange={() => {
                          setSelectedAccounts((prev) =>
                            prev.includes(name) ? prev.filter((a) => a !== name) : [...prev, name],
                          );
                        }}
                        onSelect={(e) => e.preventDefault()}
                      >
                        {name}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableHead>
              {/* Account Manager filter (multi-select) */}
              <TableHead className="p-1">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="h-7 w-full text-xs justify-start font-normal">
                      {selectedAMs.length === 0 ? '—' : `${selectedAMs.length} selected`}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-48 max-h-64 overflow-y-auto">
                    {uniqueAMs.map((name) => (
                      <DropdownMenuCheckboxItem
                        key={name}
                        checked={selectedAMs.includes(name)}
                        onCheckedChange={() => {
                          setSelectedAMs((prev) =>
                            prev.includes(name) ? prev.filter((a) => a !== name) : [...prev, name],
                          );
                        }}
                        onSelect={(e) => e.preventDefault()}
                      >
                        {name}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableHead>
              {/* Total — no filter */}
              <TableHead className="p-1" />
              {/* Balance — no filter */}
              <TableHead className="p-1" />
              {/* Status filter (multi-select) */}
              <TableHead className="p-1">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="h-7 w-full text-xs justify-start font-normal">
                      {selectedStatuses.length === 0 ? 'All' : `${selectedStatuses.length} selected`}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-36">
                    {([
                      { value: 'preparation', label: 'Prep' },
                      { value: 'sent', label: 'Sent' },
                      { value: 'paid', label: 'Paid' },
                      { value: 'expired', label: 'Expired' },
                      { value: 'cancelled', label: 'Cancelled' },
                    ] as const).map((opt) => (
                      <DropdownMenuCheckboxItem
                        key={opt.value}
                        checked={selectedStatuses.includes(opt.value)}
                        onCheckedChange={() => {
                          setSelectedStatuses((prev) =>
                            prev.includes(opt.value) ? prev.filter((s) => s !== opt.value) : [...prev, opt.value],
                          );
                          setPage(0);
                        }}
                        onSelect={(e) => e.preventDefault()}
                      >
                        {opt.label}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableHead>
              {/* Docs — no filter */}
              <TableHead className="p-1" />
            </TableRow>
            {/* ROW 2: Column labels + sort */}
            <TableRow>
              <TableHead className="w-10 text-center">Sync</TableHead>
              <SortableHead label="Invoice #" sortKey="invoiceNumber" currentSortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <SortableHead label="Date" sortKey="date" currentSortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <SortableHead label="Due" sortKey="dueDate" currentSortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <SortableHead label="Account" sortKey="account" currentSortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <TableHead className="text-xs">Acct Mgr</TableHead>
              <SortableHead label="Total" sortKey="total" currentSortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
              <SortableHead label="Balance" sortKey="balance" currentSortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
              <SortableHead label="Status" sortKey="status" currentSortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <TableHead className="w-16 text-center">Docs</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 10 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: colCount }).map((__, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : error ? (
              <TableRow>
                <TableCell colSpan={colCount} className="text-center text-red-600 py-8">
                  {(error as Error).message}
                </TableCell>
              </TableRow>
            ) : invoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colCount} className="text-center text-muted-foreground py-8">
                  No invoices found.
                </TableCell>
              </TableRow>
            ) : (
              invoices.map((inv) => (
                <TableRow
                  key={inv.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => setSelectedInvoiceNumber(inv.invoiceNumber)}
                >
                  <TableCell className="text-center"><SyncDot synced={inv.synced} source="atek" /></TableCell>
                  <TableCell className="font-mono text-xs">{inv.invoiceNumber}</TableCell>
                  <TableCell>{inv.date ? formatDate(inv.date) : '—'}</TableCell>
                  <TableCell className={cn(inv.overdue && 'text-red-600 font-medium')}>{inv.dueDate ? formatDate(inv.dueDate) : '—'}</TableCell>
                  <TableCell className="max-w-[200px] truncate">{inv.account || '—'}</TableCell>
                  <TableCell className="text-sm text-muted-foreground truncate max-w-[120px]">{inv.accountManager || '—'}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{formatCurrency(inv.total, inv.currency)}</TableCell>
                  <TableCell className={cn('text-right tabular-nums', inv.balance > 0 ? 'text-orange-600' : 'text-muted-foreground')}>{formatCurrency(inv.balance, inv.currency)}</TableCell>
                  <TableCell>
                    <Badge variant="default" className={cn(
                      inv.status === 'paid' ? 'bg-green-600 hover:bg-green-700'
                        : inv.overdue ? 'bg-red-600 text-white hover:bg-red-700'
                        : inv.status === 'sent' ? 'bg-blue-600 hover:bg-blue-700'
                        : inv.status === 'cancelled' ? 'bg-red-600 hover:bg-red-700'
                        : inv.status === 'expired' ? 'bg-orange-600 hover:bg-orange-700'
                        : 'bg-gray-500 hover:bg-gray-600',
                    )}>
                      {inv.overdue ? 'Overdue' : inv.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    {inv.documentCount > 0 ? (
                      <span className="inline-flex items-center gap-1 text-muted-foreground">
                        <Paperclip className="h-3.5 w-3.5" />
                        <span className="text-xs">{inv.documentCount}</span>
                      </span>
                    ) : '—'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Footer: Per page + count + Prev/Next. Always visible so user can
          reduce pageSize even when results fit a single page. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          {total > 0 ? (
            <span>Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)} of {total}</span>
          ) : (
            <span>No results</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>Per page</span>
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(0); }}>
              <SelectTrigger className="h-7 w-20 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {totalPages > 1 && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 0}>Previous</Button>
              <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}>Next</Button>
            </div>
          )}
        </div>
      </div>

      {/* Comparison Sheet */}
      <ComparisonSheet
        open={!!selectedInvoiceNumber}
        onOpenChange={(open) => { if (!open) setSelectedInvoiceNumber(null); }}
        atekInvoice={atekDetailQuery.data?.invoice ?? null}
        atekV4={selectedV4}
        qboInvoice={qboDetail}
        isLoadingAtek={!!selectedV4?.mongoInvoiceId && atekDetailQuery.isLoading}
        isLoadingQbo={isLoadingQbo}
        invoiceId={selectedV4?.id || null}
        invoiceNumber={selectedInvoiceNumber || ''}
      />
    </div>
  );
}

// =============================================================================
// Page (tabbed) — fetches cross-reference sets for sync dots
// =============================================================================

type InvoiceTab = 'sync' | 'atek' | 'qbo' | 'analysis';

const INVOICE_TABS: readonly UnderlineTabsTab<InvoiceTab>[] = [
  { value: 'sync', label: 'Sync' },
  { value: 'atek', label: 'ATEK' },
  { value: 'qbo', label: 'QuickBooks' },
  { value: 'analysis', label: 'Analysis' },
] as const;

const VALID_INVOICE_TABS: readonly InvoiceTab[] = ['sync', 'atek', 'qbo', 'analysis'];

export default function QboInvoicesPage() {
  // Deep-link support: /acc/invoices/<number> opens the comparison sheet for
  // that invoice on the sync tab. The SyncTab reads `useParams` directly to
  // sync URL ↔ selectedInvoiceNumber state. Here we just force the tab to
  // 'sync' when a URL param is present so the sheet actually renders.
  const { invoiceNumber: urlInvoiceNumber } = useParams<{ invoiceNumber?: string }>();

  // Migration-aware default: if the new shared-hook key is empty on first load,
  // fall back to the legacy `atek-acc-invoices-filters.tab` so existing users
  // don't lose their previously-selected tab when this refactor lands.
  const legacyDefaultTab: InvoiceTab = (() => {
    const legacy = loadPersistedFilters().tab;
    return legacy && (VALID_INVOICE_TABS as readonly string[]).includes(legacy)
      ? (legacy as InvoiceTab)
      : 'sync';
  })();
  const [activeTab, setActiveTab] = usePersistedTab<InvoiceTab>('acc_invoices', VALID_INVOICE_TABS, legacyDefaultTab);

  // Persist tab into the legacy filter store too, since other tab components read from it.
  const handleTabChange = (next: InvoiceTab) => {
    setActiveTab(next);
    savePersistedFilters({ tab: next });
  };

  // Force sync tab when deep-linked from /acc/invoices/<number>.
  useEffect(() => {
    if (urlInvoiceNumber && activeTab !== 'sync') {
      setActiveTab('sync');
    }
  }, [urlInvoiceNumber, activeTab, setActiveTab]);

  // Lightweight sync map from ATEK — all invoice numbers + quickbooksInvoiceId (Redis cached)
  const syncMapQuery = useQuery({
    queryKey: ['atek-invoices-sync-map'],
    queryFn: () => api.get<{ invoices: Array<{ invoiceNumber: string; quickbooksInvoiceId: string | null }> }>('/v4/invoices/sync-map'),
    staleTime: 0,
  });

  // Set of ATEK invoice numbers (for QBO tab: "is this QBO invoice in ATEK?")
  const atekInvoiceNumbers = useMemo(() => {
    return new Set((syncMapQuery.data?.invoices ?? []).map((i) => i.invoiceNumber));
  }, [syncMapQuery.data]);

  // Set of QBO invoice IDs that ATEK knows about (for ATEK tab: "is this ATEK invoice in QBO?")
  // Uses quickbooksInvoiceId from PG + invoice number match from QBO Redis cache
  const qboQuery = useQuery({
    queryKey: ['quickbooks-invoices-all-numbers'],
    queryFn: async () => {
      const res = await api.get<{ invoices: Array<{ docNumber: string }> }>('/v4/quickbooks');
      return res.invoices?.map((i) => i.docNumber) ?? [];
    },
    staleTime: 0,
  });

  const qboInvoiceNumbers = useMemo(() => new Set(qboQuery.data ?? []), [qboQuery.data]);

  return (
    <PageShell
      Icon={Receipt}
      subtitle="Reconcile ATEK invoices (Mongo + Postgres) against QuickBooks"
      tabs={<UnderlineTabs tabs={INVOICE_TABS} active={activeTab} onChange={handleTabChange} />}
    >
      {activeTab === 'sync' && (
        <SyncTab syncMap={syncMapQuery.data?.invoices ?? []} qboInvoiceNumbers={qboInvoiceNumbers} />
      )}
      {activeTab === 'atek' && <AtekTab qboInvoiceNumbers={qboInvoiceNumbers} />}
      {activeTab === 'qbo' && <QboTab atekInvoiceNumbers={atekInvoiceNumbers} />}
      {activeTab === 'analysis' && <AnalysisTab />}
    </PageShell>
  );
}
